/**
 * MobileJournal.jsx — Production v10 (World-Class Engagement Upgrade)
 *
 * CHANGES vs v9.2
 * ─────────────────────────────────────────────────────────────────────────
 * [ENGAGEMENT] Like, comment count, share on EVERY card — no modal needed
 * [ENGAGEMENT] ShareSheet — native share / WhatsApp / copy link
 * [ENGAGEMENT] Inline like with heart animation + optimistic update
 * [LAYOUT]     Compact navigation — merged tabs + themes into single strip
 * [LAYOUT]     Search moved to expandable overlay (🔍 icon)
 * [LAYOUT]     Write moved to header button (not inline card)
 * [LAYOUT]     Mixed card layouts: full, compact horizontal, text-only
 * [LAYOUT]     Removed redundant sticky header (shell provides it)
 * [READING]    Pull quotes on text-only cards for emotional hooks
 * [READING]    Comment preview count as social proof
 * [UX]         73% content visibility (was 55%)
 *
 * ALL v9.2 logic preserved: wellness report, StoryModal, WriteStoryForm,
 * hooks, helpers, cache, theme art, bad-word filter, comments.
 * ─────────────────────────────────────────────────────────────────────────
 */

import React, {
  useState, useEffect, useLayoutEffect, useRef,
  useCallback, useMemo,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../../firebase";
import {
  collection, addDoc, getDocs, getDoc, doc,
  setDoc, deleteDoc, updateDoc, increment,
  query, orderBy, serverTimestamp, onSnapshot,
  limit, startAfter, where,
} from "firebase/firestore";
import DOMPurify from "dompurify";

// ── Wellness report imports ──────────────────────────────────────────────────
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from "recharts";
import { authFetch, getUserClaims } from "../../utils/authFetch";
import WellnessModal from "../../components/WellnessModal";

// ─── Constants ────────────────────────────────────────────────────────────────
const PAGE_SIZE = 20;

// ── Wellness Report colour palette (theme-aware — #21 fix) ────────────────────
const C_LIGHT = {
  ink:    "#1a1612", ink2:   "#2e2820", ink3: "#5a4e3e", ink4: "#8c7f6d",
  ink5:   "#bfb5a6", paper:  "#faf8f3", paper2: "#f3efe6", paper3: "#e8e2d6",
  accent: "#c95b3d", accentL:"#fdf2ee",
  green:  "#1a6b3c", greenL: "#e6f4ec",
  blue:   "#1a4f8a", blueL:  "#e6eef8",
  purple: "#5a3a8a", purpleL:"#efe8f8",
  amber:  "#8a5a12", amberL: "#fdf3e0",
  teal:   "#0d6e6e", tealL:  "#e0f5f5",
};
const C_DARK = {
  ink:    "#F0EDE8", ink2:   "#DDD8CF", ink3: "#B0A898", ink4: "#8C8477",
  ink5:   "#5A5449", paper:  "#1A1D24", paper2: "#22262F", paper3: "#2E323C",
  accent: "#E8754F", accentL:"#2D1F18",
  green:  "#4CAF7C", greenL: "#1A2E22",
  blue:   "#5B8FCC", blueL:  "#1A2433",
  purple: "#9B7FCC", purpleL:"#231A33",
  amber:  "#CC9B42", amberL: "#2D2412",
  teal:   "#4DB8B8", tealL:  "#1A2E2E",
};
function getC() {
  try { return document.body.classList.contains("dark-theme") ? C_DARK : C_LIGHT; }
  catch { return C_LIGHT; }
}
// For module-level code that runs at import time, use light. 
// Components call getC() at render time to get current theme.
const C = C_LIGHT;

// ── SVG progress arc ─────────────────────────────────────────────────────────
const Arc = ({ value, max = 10, color, size = 72, label }) => {
  const C = getC();
  const r    = (size - 10) / 2;
  const circ = 2 * Math.PI * r;
  const pct  = Math.min(value / max, 1);
  const dash = pct * circ;
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
      <svg width={size} height={size} style={{ transform:"rotate(-90deg)" }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={C.paper3} strokeWidth={6}/>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={6}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          style={{ transition:"stroke-dasharray 1.2s cubic-bezier(.4,0,.2,1)" }}/>
        <text x={size/2} y={size/2+1} textAnchor="middle" dominantBaseline="middle"
          style={{ transform:`rotate(90deg)`, transformOrigin:`${size/2}px ${size/2}px` }}
          fill={C.ink} fontSize={16} fontWeight={700} fontFamily="Georgia,serif">{value}</text>
      </svg>
      <div style={{ fontSize:10, color:C.ink4, fontWeight:500, textAlign:"center", maxWidth:64 }}>{label}</div>
    </div>
  );
};

// ── Mood heatmap ─────────────────────────────────────────────────────────────
const MoodHeatmap = ({ trendData, month }) => {
  const C = getC();
  if (!trendData || trendData.length === 0) return null;
  const [y, m] = (month || "").split("-").map(Number);
  const total  = y && m ? new Date(Date.UTC(y, m, 0)).getUTCDate() : 31;
  const byDay  = {};
  trendData.forEach(d => {
    const day = parseInt(d.date?.split("-")[2] || "0");
    if (day) byDay[day] = { score: d.moodScore, mood: d.dominantMood };
  });
  const scoreToColor  = s => s >= 8 ? "#1a6b3c20" : s >= 6 ? "#26a69a20" : s >= 4 ? "#ff980020" : "#ef535020";
  const scoreToBorder = s => s >= 8 ? "#1a6b3c"   : s >= 6 ? "#26a69a"   : s >= 4 ? "#ff9800"   : "#ef5350";
  return (
    <div style={{ display:"grid", gridTemplateColumns:"repeat(7, 1fr)", gap:4 }}>
      {["S","M","T","W","T","F","S"].map((d,i) => (
        <div key={i} style={{ textAlign:"center", fontSize:9, color:C.ink5, fontWeight:500, paddingBottom:2 }}>{d}</div>
      ))}
      {Array.from({ length: total }, (_,i) => i+1).map(day => {
        const data = byDay[day];
        return (
          <div key={day} style={{
            aspectRatio:"1", borderRadius:6, display:"flex", alignItems:"center",
            justifyContent:"center", fontSize:10, fontWeight:500,
            background: data ? scoreToColor(data.score)  : C.paper2,
            border:     `1px solid ${data ? scoreToBorder(data.score) : C.paper3}`,
            color: C.ink3,
          }}>{day}</div>
        );
      })}
    </div>
  );
};

// ── Mood distribution bar ─────────────────────────────────────────────────────
const MoodDistBar = ({ trendData }) => {
  const C = getC();
  if (!trendData || trendData.length === 0) return null;
  const zones = [
    { label:"Great (8-10)", color:"#1a6b3c", range:[8,10]  },
    { label:"Good (6-7)",   color:"#26a69a", range:[6,7.9] },
    { label:"Okay (4-5)",   color:"#ff9800", range:[4,5.9] },
    { label:"Low (0-3)",    color:"#ef5350", range:[0,3.9] },
  ];
  const total  = trendData.length;
  const counts = zones.map(z => ({
    ...z,
    count: trendData.filter(d => d.moodScore >= z.range[0] && d.moodScore <= z.range[1]).length,
    pct:   Math.round((trendData.filter(d => d.moodScore >= z.range[0] && d.moodScore <= z.range[1]).length / total) * 100),
  }));
  return (
    <div>
      <div style={{ display:"flex", height:10, borderRadius:6, overflow:"hidden", marginBottom:10 }}>
        {counts.map(z => z.pct > 0 && (
          <div key={z.label} style={{ width:`${z.pct}%`, background:z.color, transition:"width 1s" }}/>
        ))}
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"6px 12px" }}>
        {counts.map(z => (
          <div key={z.label} style={{ display:"flex", alignItems:"center", gap:6, fontSize:11 }}>
            <div style={{ width:8, height:8, borderRadius:"50%", background:z.color, flexShrink:0 }}/>
            <div style={{ flex:1, color:C.ink3 }}>{z.label}</div>
            <div style={{ fontWeight:500, color:C.ink }}>{z.pct}%</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Branded report header ─────────────────────────────────────────────────────
const ReportHeader = ({ month, reportType, weekNumber, userName }) => {
  const C = getC();
  return (
  <div style={{ margin:"0 16px 14px", borderRadius:20, background:C.ink, overflow:"hidden" }}>
    <div style={{ padding:"20px 20px 16px", borderBottom:"1px solid rgba(255,255,255,.08)" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ width:32, height:32, borderRadius:10, background:C.accent, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16 }}>💖</div>
          <div>
            <div style={{ fontFamily:'"Playfair Display",Georgia,serif', fontSize:16, fontWeight:700, color:"#fff", letterSpacing:"-.01em" }}>HealHub</div>
            <div style={{ fontSize:10, color:"rgba(255,255,255,.4)", fontWeight:300 }}>Wellness Intelligence</div>
          </div>
        </div>
        <div style={{ textAlign:"right" }}>
          <div style={{ fontSize:11, color:"rgba(255,255,255,.5)", fontWeight:300 }}>Generated</div>
          <div style={{ fontSize:11, color:"rgba(255,255,255,.6)", fontWeight:500 }}>
            {new Date().toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" })}
          </div>
        </div>
      </div>
      <div style={{ fontSize:11, color:"rgba(255,255,255,.35)", marginBottom:4, fontWeight:300, letterSpacing:".06em", textTransform:"uppercase" }}>
        {reportType === "weekly" ? `Week ${weekNumber} of ` : "Monthly Report · "}{month}
      </div>
      <div style={{ fontFamily:'"Playfair Display",Georgia,serif', fontSize:22, fontWeight:900, color:"#fff", lineHeight:1.2 }}>
        Your Emotional Wellness Report
      </div>
      {userName && (
        <div style={{ fontSize:12, color:"rgba(255,255,255,.45)", marginTop:6, fontWeight:300 }}>Prepared for {userName}</div>
      )}
    </div>
    <div style={{ padding:"10px 20px", display:"flex", gap:6, flexWrap:"wrap" }}>
      {["Powered by AI · Not medical advice", "Private & encrypted", "iCall: 9152987821"].map((t,i) => (
        <div key={i} style={{ fontSize:9, color:"rgba(255,255,255,.3)", padding:"3px 8px", borderRadius:10, background:"rgba(255,255,255,.06)", fontWeight:400 }}>{t}</div>
      ))}
    </div>
  </div>
);};

// ── Teaser for free users ─────────────────────────────────────────────────────
const ReportTeaser = ({ onGenerate, onUpgrade }) => {
  const C = getC();
  return (
  <div style={{ position:"relative", marginTop:8 }}>
    <div style={{ filter:"blur(5px)", pointerEvents:"none", userSelect:"none", padding:"0 16px" }}>
      <div style={{ borderRadius:20, padding:22, background:C.ink, marginBottom:12 }}>
        <div style={{ fontSize:11, color:"rgba(255,255,255,.4)", marginBottom:6 }}>March 2026 · Monthly Wellness Report</div>
        <div style={{ display:"flex", alignItems:"baseline", gap:4 }}>
          <div style={{ fontFamily:"Georgia,serif", fontSize:58, fontWeight:900, color:"#fff", lineHeight:1 }}>7.4</div>
          <div style={{ fontSize:18, color:"rgba(255,255,255,.35)" }}>/10</div>
        </div>
        <div style={{ fontSize:12, color:"rgba(255,255,255,.45)", marginTop:6 }}>Above average — you're doing better than you think.</div>
      </div>
      {["Hopefulness · 8.1","Stress · 4.2","Stability · 7.0","Awareness · 8.8"].map((t,i) => (
        <div key={i} style={{ borderRadius:14, padding:14, border:`1px solid ${C.paper3}`, background:"#fff", marginBottom:8, display:"flex", justifyContent:"space-between" }}>
          <div style={{ fontSize:12, color:C.ink4 }}>{t.split(" · ")[0]}</div>
          <div style={{ fontSize:18, fontWeight:700, color:C.ink, fontFamily:"Georgia,serif" }}>{t.split(" · ")[1]}</div>
        </div>
      ))}
    </div>
    <div style={{ position:"absolute", inset:0, background:`linear-gradient(to bottom, transparent 0%, ${C.paper}bb 35%, ${C.paper}f8 60%)`, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"flex-end", paddingBottom:24 }}>
      <div style={{ background:"#fff", borderRadius:20, border:`1px solid ${C.paper3}`, padding:"22px 20px", margin:"0 16px", width:"calc(100% - 32px)", textAlign:"center" }}>
        <div style={{ fontFamily:'"Playfair Display",Georgia,serif', fontSize:17, fontWeight:700, color:C.ink, marginBottom:8 }}>Your report is waiting</div>
        <div style={{ fontSize:13, color:C.ink4, lineHeight:"1.65", marginBottom:18, fontWeight:300 }}>
          Generate your 1 free lifetime wellness report — score, patterns, strengths, mood trends and a personal message.
        </div>
        <button onClick={onGenerate} style={{ width:"100%", padding:14, background:C.ink, color:C.paper, border:"none", borderRadius:14, fontSize:14, fontWeight:500, cursor:"pointer", fontFamily:"inherit", marginBottom:10 }}>
          Generate my free report ✨
        </button>
        <button onClick={onUpgrade} style={{ width:"100%", padding:11, background:"transparent", color:C.ink4, border:`1px solid ${C.paper3}`, borderRadius:14, fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>
          Upgrade for unlimited reports →
        </button>
      </div>
    </div>
  </div>
);};

// ═══════════════════════════════════════════════════════════════════════════════
// WELLNESS REPORT SHEET — full-screen portal, mirrors Insight.jsx
// ═══════════════════════════════════════════════════════════════════════════════
function WellnessReportSheet({ onClose }) {
  const navigate = useNavigate();
  const C = getC(); // #21 fix: read theme at render time

  const [isPaid,             setIsPaid]             = useState(false);
  const [isAdmin,            setIsAdmin]            = useState(false);
  const [tierLoaded,         setTierLoaded]         = useState(false);
  const [userName,           setUserName]           = useState("");

  const [reportData,         setReportData]         = useState(null);
  const [aiLoading,          setAiLoading]          = useState(false);
  const [reportError,        setReportError]        = useState("");
  const [reportLimitReached, setReportLimitReached] = useState(false);
  const [showTeaser,         setShowTeaser]         = useState(false);
  const [cached,             setCached]             = useState(false);

  const [reportType,  setReportType]  = useState("monthly");
  const [reportMonth, setReportMonth] = useState(new Date().toISOString().slice(0, 7));
  const [weekNumber,  setWeekNumber]  = useState(1);

  const [showWellnessModal, setShowWellnessModal] = useState(false);
  const [trendData,         setTrendData]         = useState([]);

  const reportRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    const fn = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  useEffect(() => {
    if (!document.getElementById("hh-gfonts-wr")) {
      const l = document.createElement("link");
      l.id = "hh-gfonts-wr"; l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,400&family=DM+Sans:wght@300;400;500&display=swap";
      document.head.appendChild(l);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      if (!auth.currentUser) return;
      const claims = await getUserClaims();
      setIsPaid(claims.paid);
      setIsAdmin(claims.isAdmin || false);
      setUserName(auth.currentUser?.displayName?.split(" ")[0] || "");
      setTierLoaded(true);

      if (!claims.paid && !claims.isAdmin) {
        try {
          const u = await authFetch("/api/usage").then(r => r.json());
          setIsAdmin(u.isAdmin || false);
          if ((u.report?.used || 0) >= 1) setReportLimitReached(true);
          else setShowTeaser(true);
        } catch { /**/ }
      }

      try {
        const res  = await authFetch("/api/report/mood-trend?days=30");
        const data = await res.json();
        setTrendData(data.trendData || []);
      } catch { /**/ }
    };
    init();
  }, []);

  const generateReport = async () => {
    setAiLoading(true); setReportError(""); setShowTeaser(false);
    try {
      const body = { reportType, month: reportMonth, ...(reportType === "weekly" ? { weekNumber } : {}) };
      const res  = await authFetch("/api/report/generate", { method:"POST", body: JSON.stringify(body) });
      const data = await res.json();
      if (res.status === 429 || res.status === 403) {
        setReportLimitReached(true);
        setReportError(data.error || "Report limit reached.");
        return;
      }
      if (!res.ok) { setReportError(data.error || "Failed to generate."); return; }
      setReportData(data.report);
      setCached(data.cached || false);
    } catch { setReportError("Network error. Please try again."); }
    finally { setAiLoading(false); }
  };

  const isUnlimited = isPaid || isAdmin;

  const monthOptions = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(); d.setMonth(d.getMonth() - i);
    return d.toISOString().slice(0, 7);
  });

  const radarData = reportData ? [
    { subject:"Hope",      value: reportData.metrics?.hope               || 0, fullMark:10 },
    { subject:"Stability", value: reportData.metrics?.emotional_stability || 0, fullMark:10 },
    { subject:"Awareness", value: reportData.metrics?.self_awareness      || 0, fullMark:10 },
    { subject:"Calm",      value: Math.max(0, 10-(reportData.metrics?.stress||5)), fullMark:10 },
  ] : [];

  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (!reportData || !reportRef.current || downloading) return;
    setDownloading(true);
    try {
    const pdfDoc = new jsPDF({ unit:"mm", format:"a4" });
    let y = 15;
    const checkBreak = (n=20) => { if (y+n > 280) { pdfDoc.addPage(); y = 15; } };
    pdfDoc.setFillColor(26,22,18);
    pdfDoc.rect(0,0,210,40,"F");
    pdfDoc.setTextColor(255,255,255);
    pdfDoc.setFontSize(18); pdfDoc.setFont("helvetica","bold");
    pdfDoc.text("HealHub",15,18);
    pdfDoc.setFontSize(9); pdfDoc.setFont("helvetica","normal");
    pdfDoc.setTextColor(180,170,160);
    pdfDoc.text("Emotional Wellness Report",15,25);
    pdfDoc.text(`${reportType==="weekly"?`Week ${weekNumber} · `:""}${reportMonth}`,15,31);
    pdfDoc.setTextColor(120,112,104);
    pdfDoc.text(`Generated ${new Date().toLocaleDateString("en-IN",{day:"numeric",month:"long",year:"numeric"})}`,15,37);
    y = 50;
    try {
      const canvas = await html2canvas(reportRef.current, { scale:1.5, useCORS:true, backgroundColor:"#faf8f3" });
      const imgW   = 180;
      const imgH   = canvas.height*(imgW/canvas.width);
      const pageH  = 240;
      const totalPages = Math.ceil(imgH/pageH);
      for (let p=0; p<totalPages; p++) {
        if (p>0) { pdfDoc.addPage(); y=15; }
        const srcY = p*(canvas.height/totalPages);
        const srcH = canvas.height/totalPages;
        const seg  = document.createElement("canvas");
        seg.width  = canvas.width;
        seg.height = srcH;
        seg.getContext("2d").drawImage(canvas,0,srcY,canvas.width,srcH,0,0,canvas.width,srcH);
        pdfDoc.addImage(seg.toDataURL("image/png"),"PNG",15,y,imgW,pageH);
      }
    } catch {
      y = 50;
      [
        { title:"Wellness Score",        content:`${reportData.wellness_score}/10` },
        { title:"Emotional Overview",    content: reportData.emotional_overview },
        { title:"Patterns Noticed",      content: reportData.patterns_affecting_you,     isList:true },
        { title:"Strengths You Showed",  content: reportData.strengths_you_showed,       isList:true },
        { title:"What Could Have Helped",content: reportData.what_could_have_helped,     isList:true },
        { title:"Encouragement",         content: reportData.gentle_encouragement },
      ].forEach(sec => {
        checkBreak(30);
        pdfDoc.setFontSize(12); pdfDoc.setTextColor(26,22,18); pdfDoc.setFont("helvetica","bold");
        pdfDoc.text(sec.title,15,y); y+=8;
        pdfDoc.setFontSize(9); pdfDoc.setFont("helvetica","normal"); pdfDoc.setTextColor(50,50,50);
        if (sec.isList) {
          (sec.content||[]).forEach(item => {
            const lines = pdfDoc.splitTextToSize(`• ${item}`,165);
            checkBreak(lines.length*5); pdfDoc.text(lines,20,y); y+=lines.length*5+2;
          });
        } else {
          const lines = pdfDoc.splitTextToSize(sec.content||"",170);
          lines.forEach(line => { checkBreak(6); pdfDoc.text(line,15,y); y+=5; });
        }
        y+=4;
      });
    }
    checkBreak(25);
    pdfDoc.setFillColor(243,239,230);
    pdfDoc.rect(10,y,190,20,"F");
    pdfDoc.setFontSize(7); pdfDoc.setTextColor(140,127,109); pdfDoc.setFont("helvetica","italic");
    pdfDoc.text("Disclaimer: This report is generated by AI for personal wellness reflection only.",15,y+7);
    pdfDoc.text("If you are in crisis, please call iCall: 9152987821.",15,y+12);
    pdfDoc.text(`© ${new Date().getFullYear()} HealHub · healhub.in`,15,y+18);
    pdfDoc.save(`HealHub_Wellness_Report_${reportMonth}.pdf`);
    } catch (e) { console.error("PDF generation failed:", e); }
    finally { setDownloading(false); }
  };

  return createPortal(
    <div style={{
      position:"fixed", inset:0, zIndex:200,
      background: C.paper,
      fontFamily:'"DM Sans",system-ui,sans-serif',
      display:"flex", flexDirection:"column",
      animation:"mjSlideUp .3s cubic-bezier(0.25,1,0.4,1)",
    }}>

      <div style={{
        position:"sticky", top:0, zIndex:20,
        background: C.paper, borderBottom:`0.5px solid ${C.paper3}`,
        display:"flex", alignItems:"center", justifyContent:"space-between",
        padding:"12px 16px",
      }}>
        <button
          onClick={onClose}
          style={{
            width:36, height:36, borderRadius:"50%", background:C.paper2,
            border:`1px solid ${C.paper3}`, display:"flex", alignItems:"center",
            justifyContent:"center", cursor:"pointer",
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.ink3} strokeWidth="2">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </button>
        <div style={{ fontFamily:'"Playfair Display",Georgia,serif', fontSize:18, fontWeight:700, color:C.ink, letterSpacing:"-.01em" }}>
          Wellness Report
        </div>
        <button
          onClick={() => setShowWellnessModal(true)}
          style={{
            width:36, height:36, borderRadius:"50%", background:C.paper2,
            border:`1px solid ${C.paper3}`, display:"flex", alignItems:"center",
            justifyContent:"center", cursor:"pointer", fontSize:16,
          }}
        >💡</button>
      </div>

      {showWellnessModal && <WellnessModal onClose={() => setShowWellnessModal(false)}/>}

      {isAdmin && (
        <div style={{ margin:"10px 16px 0", padding:"8px 14px", borderRadius:12, background:"#1a1612", display:"flex", alignItems:"center", gap:8 }}>
          <div style={{ width:6, height:6, borderRadius:"50%", background:"#26a69a" }}/>
          <div style={{ fontSize:12, color:"rgba(255,255,255,.7)", fontWeight:500 }}>Admin · Unlimited access · No plan required</div>
        </div>
      )}

      <div style={{ flex:1, overflowY:"auto", WebkitOverflowScrolling:"touch", paddingBottom:40 }}>

        <div style={{ padding:"16px 16px 0" }}>
          {isUnlimited && (
            <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:12 }}>
              <select value={reportType} onChange={e => setReportType(e.target.value)}
                style={{ flex:1, padding:"10px 12px", borderRadius:12, border:`1px solid ${C.paper3}`, background:"#fff", color:C.ink, fontSize:13, fontFamily:"inherit" }}>
                <option value="monthly">Monthly report</option>
                <option value="weekly">Weekly report</option>
              </select>
              <select value={reportMonth} onChange={e => setReportMonth(e.target.value)}
                style={{ flex:1, padding:"10px 12px", borderRadius:12, border:`1px solid ${C.paper3}`, background:"#fff", color:C.ink, fontSize:13, fontFamily:"inherit" }}>
                {monthOptions.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              {reportType === "weekly" && (
                <select value={weekNumber} onChange={e => setWeekNumber(Number(e.target.value))}
                  style={{ padding:"10px 12px", borderRadius:12, border:`1px solid ${C.paper3}`, background:"#fff", color:C.ink, fontSize:13, fontFamily:"inherit" }}>
                  {[1,2,3,4].map(w => <option key={w} value={w}>Week {w}</option>)}
                </select>
              )}
            </div>
          )}

          {reportLimitReached && !isUnlimited && (
            <div style={{ padding:14, borderRadius:14, background:C.accentL, border:`1px solid ${C.accent}40`, marginBottom:12 }}>
              <div style={{ fontSize:13, color:"#a05030", marginBottom:10 }}>You've used your 1 free lifetime report.</div>
              <button onClick={() => { onClose(); navigate("/pricing"); }}
                style={{ width:"100%", padding:11, background:C.accent, color:"#fff", border:"none", borderRadius:10, fontSize:13, fontWeight:500, cursor:"pointer", fontFamily:"inherit" }}>
                Upgrade for unlimited reports — ₹35/month →
              </button>
            </div>
          )}

          {reportError && (
            <div style={{ padding:12, borderRadius:12, background:"#ffebee", color:"#b71c1c", fontSize:13, marginBottom:12 }}>
              {reportError}
            </div>
          )}

          <button
            onClick={generateReport}
            disabled={aiLoading || (reportLimitReached && !isUnlimited)}
            style={{
              width:"100%", padding:14,
              background: aiLoading || (reportLimitReached && !isUnlimited) ? C.paper3 : C.ink,
              color:       aiLoading || (reportLimitReached && !isUnlimited) ? C.ink5  : C.paper,
              border:"none", borderRadius:14, fontSize:14, fontWeight:500,
              cursor: aiLoading || (reportLimitReached && !isUnlimited) ? "not-allowed" : "pointer",
              fontFamily:"inherit", transition:"all .18s", marginBottom:4,
            }}
          >
            {aiLoading ? "✨ Generating your report..." : reportData ? "🔄 Regenerate report" : "✨ Generate my report"}
          </button>
          {cached && reportData && (
            <div style={{ textAlign:"center", fontSize:11, color:C.ink5, marginTop:4 }}>⚡ Saved report for {reportMonth}</div>
          )}
        </div>

        {!reportData && !aiLoading && showTeaser && (
          <ReportTeaser
            onGenerate={() => { setShowTeaser(false); generateReport(); }}
            onUpgrade={() => { onClose(); navigate("/pricing"); }}
          />
        )}

        {reportData && (
          <div ref={reportRef} style={{ marginTop:16 }}>

            {reportData.crisis_message && (
              <div style={{ margin:"0 16px 12px", padding:16, borderRadius:16, background:C.blueL, border:`1px solid ${C.blue}40` }}>
                <div style={{ fontSize:13, color:C.blue, lineHeight:"1.7", marginBottom:10 }}>{reportData.crisis_message}</div>
                <a href="tel:9152987821" style={{ display:"block", padding:11, background:C.blue, color:"#fff", borderRadius:10, textDecoration:"none", textAlign:"center", fontSize:13, fontWeight:500 }}>
                  📞 iCall — 9152987821
                </a>
              </div>
            )}

            <ReportHeader month={reportMonth} reportType={reportType} weekNumber={weekNumber} userName={userName}/>

            <div style={{ margin:"0 16px 12px", borderRadius:20, padding:"24px 20px", background:C.ink, position:"relative", overflow:"hidden" }}>
              <div style={{ position:"absolute", width:160, height:160, borderRadius:"50%", background:"rgba(201,91,61,.1)", top:-50, right:-30, pointerEvents:"none" }}/>
              <div style={{ position:"absolute", width:100, height:100, borderRadius:"50%", background:"rgba(26,107,60,.08)", bottom:-30, left:-20, pointerEvents:"none" }}/>
              <div style={{ fontSize:11, fontWeight:500, letterSpacing:".08em", textTransform:"uppercase", color:"rgba(255,255,255,.4)", marginBottom:6 }}>Overall wellness score</div>
              <div style={{ display:"flex", alignItems:"flex-end", gap:8, marginBottom:8 }}>
                <div style={{ fontFamily:'"Playfair Display",Georgia,serif', fontSize:72, fontWeight:900, color:"#fff", lineHeight:1, letterSpacing:"-.03em" }}>
                  {reportData.wellness_score}
                </div>
                <div style={{ paddingBottom:10 }}>
                  <div style={{ fontSize:22, color:"rgba(255,255,255,.3)" }}>/10</div>
                </div>
              </div>
              <div style={{ fontSize:13, color:"rgba(255,255,255,.5)", marginBottom:14, fontWeight:300, lineHeight:1.6, maxWidth:280 }}>
                {reportData.mood_trend ? reportData.mood_trend.split(".")[0]+"." : "Your emotional landscape this period."}
              </div>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                {[
                  { label:`Stress ${reportData.metrics?.stress}/10`,   ok: reportData.metrics?.stress <= 4 },
                  { label:`Hope ${reportData.metrics?.hope}/10`,        ok: true },
                  { label: reportData.metrics?.emotional_stability >= 6 ? "Stable" : "Fluctuating", ok: reportData.metrics?.emotional_stability >= 6 },
                ].map((b,i) => (
                  <div key={i} style={{ padding:"5px 12px", borderRadius:20, background: b.ok ? "rgba(26,107,60,.25)" : "rgba(255,255,255,.08)", fontSize:11, color: b.ok ? "#6ee3a5" : "rgba(255,255,255,.55)", fontWeight:500 }}>
                    {b.label}
                  </div>
                ))}
              </div>
            </div>

            <div style={{ margin:"0 16px 12px", borderRadius:18, padding:"18px 16px", border:`1px solid ${C.paper3}`, background:"#fff" }}>
              <div style={{ fontSize:13, fontWeight:500, color:C.ink, marginBottom:4 }}>Key metrics</div>
              <div style={{ fontSize:11, color:C.ink4, marginBottom:18, fontWeight:300 }}>Scores out of 10 · Lower stress is better</div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:8, justifyItems:"center" }}>
                <Arc value={reportData.metrics?.hope               || 0} color="#26a69a" label="Hope"/>
                <Arc value={reportData.metrics?.emotional_stability || 0} color="#42a5f5" label="Stability"/>
                <Arc value={reportData.metrics?.self_awareness      || 0} color="#ab47bc" label="Awareness"/>
                <Arc value={reportData.metrics?.stress              || 0} color="#ef5350" label="Stress"/>
              </div>
            </div>

            {radarData.length > 0 && (
              <div style={{ margin:"0 16px 12px", borderRadius:18, padding:"16px", border:`1px solid ${C.paper3}`, background:"#fff" }}>
                <div style={{ fontSize:13, fontWeight:500, color:C.ink, marginBottom:2 }}>Emotional profile</div>
                <div style={{ fontSize:11, color:C.ink4, marginBottom:10, fontWeight:300 }}>Your strengths across 4 dimensions</div>
                <div style={{ width:"100%", height:200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData}>
                      <PolarGrid stroke={C.paper3}/>
                      <PolarAngleAxis dataKey="subject" tick={{ fontSize:11, fill:C.ink4 }}/>
                      <PolarRadiusAxis domain={[0,10]} tick={false} axisLine={false}/>
                      <Radar name="You" dataKey="value" stroke={C.accent} fill={C.accent} fillOpacity={0.15} strokeWidth={2} dot={{ r:4, fill:C.accent }}/>
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {trendData.length > 0 && (
              <div style={{ margin:"0 16px 12px", borderRadius:18, padding:"16px", border:`1px solid ${C.paper3}`, background:"#fff" }}>
                <div style={{ fontSize:13, fontWeight:500, color:C.ink, marginBottom:2 }}>Mood trend · 30 days</div>
                <div style={{ fontSize:11, color:C.ink4, marginBottom:14, fontWeight:300 }}>Daily average from diary entries and quick notes</div>
                <div style={{ width:"100%", height:160 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trendData} margin={{ top:4, right:4, bottom:0, left:-20 }}>
                      <defs>
                        <linearGradient id="moodGradWR" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor={C.accent} stopOpacity={0.2}/>
                          <stop offset="95%" stopColor={C.accent} stopOpacity={0.02}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.paper3} vertical={false}/>
                      <XAxis dataKey="label" tick={{ fontSize:9, fill:C.ink5 }} tickLine={false} axisLine={false} interval={4}/>
                      <YAxis domain={[0,10]} tick={{ fontSize:9, fill:C.ink5 }} tickLine={false} axisLine={false} ticks={[0,5,10]}/>
                      <Tooltip contentStyle={{ background:"#fff", border:`1px solid ${C.paper3}`, borderRadius:10, fontSize:12 }} formatter={v => [`${v}/10`,"Mood"]}/>
                      <Area type="monotone" dataKey="moodScore" stroke={C.accent} strokeWidth={2.5} fill="url(#moodGradWR)"
                        dot={{ r:3, fill:C.accent, strokeWidth:0 }} activeDot={{ r:5, fill:C.accent }}/>
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div style={{ marginTop:14 }}>
                  <div style={{ fontSize:11, color:C.ink4, marginBottom:10, fontWeight:500 }}>Mood distribution</div>
                  <MoodDistBar trendData={trendData}/>
                </div>
              </div>
            )}

            {trendData.length > 0 && (
              <div style={{ margin:"0 16px 12px", borderRadius:18, padding:"16px", border:`1px solid ${C.paper3}`, background:"#fff" }}>
                <div style={{ fontSize:13, fontWeight:500, color:C.ink, marginBottom:2 }}>Monthly mood calendar</div>
                <div style={{ fontSize:11, color:C.ink4, marginBottom:14, fontWeight:300 }}>Green = high mood · Red = low mood · Grey = no data</div>
                <MoodHeatmap trendData={trendData} month={reportMonth}/>
                <div style={{ display:"flex", gap:12, marginTop:10, flexWrap:"wrap" }}>
                  {[["#1a6b3c","Great (8-10)"],["#26a69a","Good (6-7)"],["#ff9800","Okay (4-5)"],["#ef5350","Low (0-3)"]].map(([c,l]) => (
                    <div key={l} style={{ display:"flex", alignItems:"center", gap:5, fontSize:10, color:C.ink4 }}>
                      <div style={{ width:10, height:10, borderRadius:3, background:`${c}25`, border:`1px solid ${c}` }}/>
                      {l}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {reportData.sentiment_overview && (
              <div style={{ margin:"0 16px 12px", borderRadius:18, padding:"16px", border:`1px solid ${C.paper3}`, background:"#fff" }}>
                <div style={{ fontSize:13, fontWeight:500, color:C.ink, marginBottom:14 }}>Emotional tone breakdown</div>
                <div style={{ display:"flex", alignItems:"center", gap:16 }}>
                  <div style={{ position:"relative", width:110, height:110, flexShrink:0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={[
                          { name:"Positive", value: reportData.sentiment_overview.positive || 0 },
                          { name:"Negative", value: reportData.sentiment_overview.negative || 0 },
                          { name:"Neutral",  value: reportData.sentiment_overview.neutral  || 0 },
                        ]} dataKey="value" innerRadius={34} outerRadius={50} paddingAngle={2}>
                          <Cell fill="#26a69a"/><Cell fill="#ef5350"/><Cell fill={C.paper3}/>
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", pointerEvents:"none" }}>
                      <div style={{ fontFamily:"Georgia,serif", fontSize:20, fontWeight:700, color:C.ink }}>{reportData.sentiment_overview.positive||0}%</div>
                      <div style={{ fontSize:9, color:C.ink4 }}>positive</div>
                    </div>
                  </div>
                  <div style={{ flex:1 }}>
                    {[
                      { label:"Positive", val: reportData.sentiment_overview.positive || 0, color:"#26a69a" },
                      { label:"Negative", val: reportData.sentiment_overview.negative || 0, color:"#ef5350" },
                      { label:"Neutral",  val: reportData.sentiment_overview.neutral  || 0, color: C.paper3 },
                    ].map(item => (
                      <div key={item.label} style={{ marginBottom:8 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, marginBottom:3 }}>
                          <span style={{ color:C.ink3 }}>{item.label}</span>
                          <span style={{ fontWeight:500, color:C.ink }}>{item.val}%</span>
                        </div>
                        <div style={{ height:5, background:C.paper2, borderRadius:3, overflow:"hidden" }}>
                          <div style={{ height:"100%", background:item.color, width:`${item.val}%`, borderRadius:3, transition:"width 1s" }}/>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {reportData.emotional_overview && (
              <div style={{ margin:"0 16px 12px", borderRadius:18, padding:"18px", border:`1px solid ${C.paper3}`, background:"#fff" }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
                  <div style={{ width:34, height:34, borderRadius:10, background:C.purpleL, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16 }}>💜</div>
                  <div style={{ fontSize:14, fontWeight:500, color:C.ink }}>Emotional overview</div>
                </div>
                <div style={{ fontSize:13, color:C.ink2, lineHeight:"1.8", fontWeight:300 }}>{reportData.emotional_overview}</div>
              </div>
            )}

            {(reportData.patterns_affecting_you || []).length > 0 && (
              <div style={{ margin:"0 16px 12px", borderRadius:18, padding:"18px", border:"1px solid #fce4ec", background:"#fffafc" }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
                  <div style={{ width:34, height:34, borderRadius:10, background:"#fce4ec", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16 }}>🔍</div>
                  <div style={{ fontSize:14, fontWeight:500, color:C.ink }}>Patterns noticed</div>
                </div>
                {reportData.patterns_affecting_you.map((p,i) => (
                  <div key={i} style={{ display:"flex", gap:12, alignItems:"flex-start", padding:"8px 0", borderBottom: i<reportData.patterns_affecting_you.length-1 ? "0.5px solid #fce4ec" : "none" }}>
                    <div style={{ width:24, height:24, borderRadius:8, background:"#fce4ec", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, color:"#b71c1c", flexShrink:0 }}>{i+1}</div>
                    <div style={{ fontSize:13, color:C.ink2, lineHeight:"1.65", fontWeight:300, paddingTop:2 }}>{p}</div>
                  </div>
                ))}
              </div>
            )}

            {(reportData.strengths_you_showed || []).length > 0 && (
              <div style={{ margin:"0 16px 12px", borderRadius:18, padding:"18px", border:`1px solid ${C.greenL}`, background:"#f8fdf9" }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
                  <div style={{ width:34, height:34, borderRadius:10, background:C.greenL, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16 }}>💪</div>
                  <div style={{ fontSize:14, fontWeight:500, color:C.ink }}>Strengths you showed</div>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                  {reportData.strengths_you_showed.map((s,i) => (
                    <div key={i} style={{ padding:"10px 12px", borderRadius:12, background:C.greenL, border:"1px solid rgba(26,107,60,.15)" }}>
                      <div style={{ fontSize:16, marginBottom:4 }}>✦</div>
                      <div style={{ fontSize:12, color:C.green, lineHeight:"1.5", fontWeight:400 }}>{s}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(reportData.what_could_have_helped || []).length > 0 && (
              <div style={{ margin:"0 16px 12px", borderRadius:18, padding:"18px", border:`1px solid ${C.blueL}`, background:"#f7fbff" }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
                  <div style={{ width:34, height:34, borderRadius:10, background:C.blueL, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16 }}>💡</div>
                  <div style={{ fontSize:14, fontWeight:500, color:C.ink }}>What could have helped</div>
                </div>
                {reportData.what_could_have_helped.map((s,i) => (
                  <div key={i} style={{ display:"flex", gap:10, alignItems:"flex-start", marginBottom:10 }}>
                    <div style={{ width:5, height:5, borderRadius:"50%", background:C.blue, flexShrink:0, marginTop:8 }}/>
                    <div style={{ fontSize:13, color:C.ink2, lineHeight:"1.65", fontWeight:300 }}>{s}</div>
                  </div>
                ))}
              </div>
            )}

            {(reportData.healhub_suggestions || []).length > 0 && (
              <div style={{ margin:"0 16px 12px", borderRadius:18, padding:"18px", border:`1px solid ${C.amberL}`, background:"#fffdf5" }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
                  <div style={{ width:34, height:34, borderRadius:10, background:C.amberL, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16 }}>✨</div>
                  <div>
                    <div style={{ fontSize:14, fontWeight:500, color:C.ink }}>HealHub features for you</div>
                    <div style={{ fontSize:10, color:C.ink4, fontWeight:300 }}>Tailored to your patterns this month</div>
                  </div>
                </div>
                {reportData.healhub_suggestions.map((s,i) => (
                  <div key={i} style={{ padding:"12px 14px", borderRadius:12, background:"#fff", border:`1px solid ${C.amberL}`, marginBottom:8, display:"flex", gap:10, alignItems:"flex-start" }}>
                    <div style={{ width:28, height:28, borderRadius:8, background:C.amberL, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, flexShrink:0 }}>💖</div>
                    <div style={{ fontSize:13, color:C.ink2, lineHeight:"1.6", fontWeight:300 }}>{s}</div>
                  </div>
                ))}
              </div>
            )}

            {reportData.gentle_encouragement && (
              <div style={{ margin:"0 16px 12px", padding:"24px 20px", borderRadius:20, background:C.purpleL, border:"1px solid rgba(90,58,138,.12)", position:"relative", overflow:"hidden" }}>
                <div style={{ position:"absolute", right:-20, top:-20, width:100, height:100, borderRadius:"50%", background:"rgba(90,58,138,.06)", pointerEvents:"none" }}/>
                <div style={{ fontSize:10, fontWeight:500, letterSpacing:".1em", textTransform:"uppercase", color:"rgba(90,58,138,.5)", marginBottom:10 }}>
                  A note from Saksham 💙
                </div>
                <div style={{ fontFamily:'"Playfair Display",Georgia,serif', fontStyle:"italic", fontSize:17, lineHeight:"1.85", color:C.purple }}>
                  "{reportData.gentle_encouragement}"
                </div>
                <div style={{ marginTop:14, fontSize:11, color:"rgba(90,58,138,.4)", fontWeight:300 }}>
                  — Saksham, your HealHub wellness companion
                </div>
              </div>
            )}

            <div style={{ padding:"0 16px 4px" }}>
              <button onClick={handleDownload} disabled={downloading} style={{ width:"100%", padding:14, background:"transparent", color: downloading ? C.ink5 : C.ink, border:`1px solid ${C.paper3}`, borderRadius:14, fontSize:14, fontWeight:500, cursor: downloading ? "not-allowed" : "pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:8, opacity: downloading ? 0.6 : 1, transition:"opacity 0.2s" }}>
                {downloading ? (
                  <>
                    <div style={{ width:16, height:16, borderRadius:"50%", border:`2px solid ${C.paper3}`, borderTopColor:C.ink4, animation:"mjSpin 0.7s linear infinite" }}/>
                    Generating PDF…
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Download branded report (PDF)
                  </>
                )}
              </button>
            </div>

            <div style={{ margin:"8px 16px 4px", padding:"16px", borderRadius:16, background:C.paper2, border:`1px solid ${C.paper3}` }}>
              <div style={{ fontSize:10, fontWeight:500, color:C.ink4, marginBottom:6, letterSpacing:".05em", textTransform:"uppercase" }}>Important disclaimer</div>
              <div style={{ fontSize:11, color:C.ink5, lineHeight:"1.75", fontWeight:300 }}>
                This report is generated by AI for personal wellness reflection only. It is <strong style={{ fontWeight:500, color:C.ink4 }}>not a medical diagnosis</strong> and is not a substitute for professional mental health care or therapy.
              </div>
              <div style={{ marginTop:10, padding:"10px 12px", borderRadius:10, background:C.blueL, border:`1px solid ${C.blue}25` }}>
                <div style={{ fontSize:11, color:C.blue, lineHeight:"1.6" }}>
                  🆘 If you or someone you know is in crisis, please reach out immediately:<br/>
                  <strong style={{ fontWeight:600 }}>iCall India — 9152987821</strong> · Available Mon–Sat, 8am–10pm
                </div>
              </div>
              <div style={{ marginTop:8, fontSize:10, color:C.ink5, textAlign:"center" }}>
                © {new Date().getFullYear()} HealHub · 💖🌿 Your space to heal, grow, and breathe.
              </div>
            </div>

          </div>
        )}

        {!isUnlimited && tierLoaded && (
          <div
            onClick={() => { onClose(); navigate("/pricing"); }}
            style={{ margin:"16px", padding:"18px 20px", borderRadius:20, background:C.accent, display:"flex", gap:14, alignItems:"center", cursor:"pointer", WebkitTapHighlightColor:"transparent" }}
          >
            <div style={{ width:40, height:40, borderRadius:12, background:"rgba(255,255,255,.15)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>⭐</div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:14, fontWeight:500, color:"#fff", marginBottom:2 }}>Unlock unlimited reports</div>
              <div style={{ fontSize:11, color:"rgba(255,255,255,.65)", fontWeight:300 }}>Plus — ₹35/month · No auto-renewal</div>
            </div>
            <div style={{ fontSize:18, color:"rgba(255,255,255,.5)" }}>›</div>
          </div>
        )}

        <div style={{ padding: "0 16px 32px", marginTop: isUnlimited ? "16px" : "0" }}>
          <button
            onClick={() => setShowWellnessModal(true)}
            style={{
              width: "100%", padding: "16px", background: "#ffffff", color: C.ink,
              border: `1px solid ${C.paper3}`, borderRadius: 16, fontSize: 14, fontWeight: 600,
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
              gap: 8, boxShadow: "0 2px 10px rgba(0,0,0,0.03)", fontFamily: "inherit"
            }}
          >
            <span style={{ fontSize: 18 }}>💡</span> 
            Check Live Wellness Score
          </button>
        </div>

      </div>
    </div>
  , document.body);
}

// ─── Helper: mergeUniqueStories (guarantees no duplicates) ───────────────────
function mergeUniqueStories(oldList, newList) {
  const map = new Map();
  [...oldList, ...newList].forEach(story => {
    if (story && story.id) map.set(story.id, story);
  });
  return Array.from(map.values());
}

// ─── Helper: get story insight (first 2-3 sentences or 150 chars) ────────────
function getStoryInsight(html) {
  if (!html) return "No insight available.";
  const plain = html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!plain) return "No insight available.";
  const sentences = plain.split(/[.!?]+/).map(s => s.trim()).filter(s => s.length > 10);
  if (sentences.length >= 2) {
    const insight = sentences.slice(0, 2).join(". ") + ".";
    return insight.length > 200 ? insight.slice(0, 197) + "…" : insight;
  }
  return plain.length > 150 ? plain.slice(0, 147) + "…" : plain;
}

// ─── Module-level Story Cache with deduplication ─────────────────────────────
const _cache = new Map();
const CACHE_TTL = 5 * 60 * 1000;

const StoryCache = {
  _key: (theme) => `t:${theme}`,
  get(theme) {
    const e = _cache.get(StoryCache._key(theme));
    if (!e) return null;
    if (Date.now() - e.ts > CACHE_TTL) { _cache.delete(StoryCache._key(theme)); return null; }
    return e;
  },
  set(theme, { stories, lastDoc, hasMore }) {
    _cache.set(StoryCache._key(theme), { stories: mergeUniqueStories([], stories), lastDoc, hasMore, ts: Date.now() });
  },
  appendPage(theme, { newDocs, newLastDoc, hasMore }) {
    const e = _cache.get(StoryCache._key(theme));
    if (!e) return;
    e.stories = mergeUniqueStories(e.stories, newDocs);
    e.lastDoc = newLastDoc;
    e.hasMore = hasMore;
  },
  prepend(story) {
    _cache.forEach((e, key) => {
      const theme = key.slice(2);
      if (theme === "all" || theme === story.theme) {
        e.stories = mergeUniqueStories([story], e.stories);
      }
    });
  },
  remove(id) {
    _cache.forEach(e => { e.stories = e.stories.filter(s => s.id !== id); });
  },
  invalidate(theme) {
    if (theme) _cache.delete(StoryCache._key(theme));
    else _cache.clear();
  },
};

// ─── Haptic Engine ────────────────────────────────────────────────────────────
const triggerHaptic = (type = "light") => {
  if (typeof window !== "undefined" && window.navigator?.vibrate) {
    window.navigator.vibrate(type === "heavy" ? [40, 30, 40] : 15);
  }
};

/* ══════════════════════════════════════════════
   CONTENT HELPERS (unchanged)
══════════════════════════════════════════════ */
const BANNED_WORDS = [
  "fuck","fucker","fucking","fucked","fucks","fuk","fck","phuck","fuuck",
  "shit","shitting","shits","shitty","shite","bitch","bitches","bich","biatch","byatch",
  "slut","sluts","whore","whores","asshole","assholes","bastard","bastards",
  "cunt","cunts","dick","dicks","dik","diq","cock","cocks","cok","pussy","pussies",
  "nigger","nigga","niggar","faggot","fag","retard","retarded","retawd",
  "motherfucker","motherfucking","rape","raping","raper",
  "kys","kill yourself","go kill yourself",
  "bhenchod","behenchod","behanchod","bhench","bhenchodd","bahenchod",
  "madarchod","madarch","madarchodd","madarchoot","madarchut","maadarchod",
  "bhosda","bhosdaa","bhosdike","bhosdiwala","bhosdiwale","bsdk","b.s.d.k.","bosadike","bosdike",
  "chutiya","chutiye","chutia","chutiyapa","chutiyaa","gandu","ganduu","gaandu",
  "lund","loda","lauda","laude","lode","loduu","chut","choot","chooot","buur","burr",
  "randi","raand","randy","randwa","harami","haraami","haramkhor","haramzada","haramjaada",
  "kutta","kutte","kutiya","kuttiya","kutti","suar","bhadwa","bhaduaa","bhadva","bhadve",
  "chodu","chodd","chud","chudai","chudne","chudwa","gand","gaand","gandi","moot",
  "chooche","chuchi","choochi","mammey","mamme","bakchod","bakchodd","kamina","kameena","kameene",
  "jhatu","jhat","jhaantu","laundi","laundiya","launda","fck","fucc","phuq",
  "balatkar","balatkari","balatkaar",
];
function normaliseLeet(t) {
  return t.replace(/@/g,"a").replace(/\$/g,"s").replace(/\*/g,"")
    .replace(/!/g,"i").replace(/0/g,"o").replace(/1/g,"i")
    .replace(/3/g,"e").replace(/4/g,"a").replace(/5/g,"s").replace(/8/g,"b");
}
function containsBannedWords(html) {
  const raw   = html.replace(/<[^>]*>/g, " ").toLowerCase();
  const plain = normaliseLeet(raw).replace(/[^a-z\s.]/g, "");
  return BANNED_WORDS.filter(w => plain.includes(normaliseLeet(w.toLowerCase())));
}
function countWords(html) {
  const plain = html.replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").trim();
  return plain === "" ? 0 : plain.split(/\s+/).filter(Boolean).length;
}
function sanitize(html) {
  return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
}
function getLastLine(html) {
  if (!html) return "";
  const plain = html.replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
  if (!plain) return "";
  const sentences = plain.split(/[.!?]+\s+/).map(s => s.trim()).filter(s => s.length > 20);
  if (!sentences.length) return plain.length > 120 ? plain.slice(0,118)+"…" : plain;
  const last = sentences[sentences.length - 1];
  return last.length > 120 ? last.slice(0,118)+"…" : last;
}

/* ══════════════════════════════════════════════
   THEME CONFIG (unchanged)
══════════════════════════════════════════════ */
export const STORY_THEMES = [
  { id:"real-journey",   label:"Real Life Journey", emoji:"🌱", color:"#3D6B57", light:"#EAF3EE", editorBg:"#F2FAF5", textColor:"#2A4F3E" },
  { id:"motivational",   label:"Motivational",      emoji:"🔥", color:"#B8421E", light:"#FAE9E4", editorBg:"#FDF4F1", textColor:"#8A2E12" },
  { id:"fiction",        label:"Fiction",           emoji:"📖", color:"#5C4E9A", light:"#EDEBF9", editorBg:"#F6F4FD", textColor:"#3D3274" },
  { id:"horror",         label:"Horror",            emoji:"👻", color:"#1E1E1E", light:"#EBEBEB", editorBg:"#F5F5F5", textColor:"#1E1E1E" },
  { id:"romance",        label:"Romance",           emoji:"💕", color:"#A83860", light:"#FAE8F0", editorBg:"#FDF2F6", textColor:"#7A2444" },
  { id:"mental-health",  label:"Mental Health",     emoji:"🧠", color:"#2D7E72", light:"#DFF2EF", editorBg:"#EFF9F7", textColor:"#1D5E55" },
  { id:"comedy",         label:"Comedy",            emoji:"😄", color:"#B07A10", light:"#FBF0D4", editorBg:"#FFFBEE", textColor:"#7A5508" },
  { id:"self-discovery", label:"Self Discovery",    emoji:"✨", color:"#3A5E9C", light:"#E4EDF9", editorBg:"#F1F6FD", textColor:"#273E6B" },
];

/* ══════════════════════════════════════════════
   ABSTRACT COVER ART (unchanged)
══════════════════════════════════════════════ */
function RealJourneyArt({ color }) { return ( <svg viewBox="0 0 380 200" preserveAspectRatio="xMidYMid slice" style={{ width:"100%", height:"100%" }}> {[200,175,150,125,100,75,50].map((r,i) => ( <ellipse key={i} cx={190} cy={260} rx={r*1.4} ry={r} fill="none" stroke={color} strokeWidth={0.7} strokeOpacity={0.08+i*0.04} strokeDasharray={`${r*1.8} ${r*0.4}`} strokeDashoffset={i*18}/> ))} <path d="M190,40 C210,20 230,30 220,10 C200,25 185,15 190,40Z" fill={color} opacity={0.22}/> <path d="M190,40 C170,20 150,30 160,10 C178,25 193,15 190,40Z" fill={color} opacity={0.14}/> <line x1="190" y1="40" x2="190" y2="130" stroke={color} strokeWidth={1} strokeOpacity={0.18}/> </svg> ); }
function MotivationalArt({ color }) { return ( <svg viewBox="0 0 380 200" preserveAspectRatio="xMidYMid slice" style={{ width:"100%", height:"100%" }}> {[0,1,2,3,4,5,6,7].map(i => ( <line key={i} x1={40+i*44} y1={200} x2={40+i*44-60} y2={-10} stroke={color} strokeWidth={1.2} strokeOpacity={0.07+i*0.015}/> ))} <polygon points="110,180 190,40 270,180" fill={color} opacity={0.15}/> <polygon points="50,180 190,10 330,180" fill="none" stroke={color} strokeWidth={1.2} strokeOpacity={0.22}/> <polygon points="80,180 190,60 300,180" fill="none" stroke={color} strokeWidth={0.8} strokeOpacity={0.12}/> <circle cx={190} cy={10} r={6} fill={color} opacity={0.35}/> </svg> ); }
function FictionArt({ color }) { return ( <svg viewBox="0 0 380 200" preserveAspectRatio="xMidYMid slice" style={{ width:"100%", height:"100%" }}> {[[120,60,80,110],[200,40,110,130],[160,100,90,100],[240,80,70,90],[100,130,60,80],[280,110,80,100]].map(([x,y,w,h],i) => ( <rect key={i} x={x-w/2} y={y-h/2} width={w} height={h} rx={3} fill={color} opacity={0.04+i*0.025} transform={`rotate(${-15+i*8},${x},${y})`}/> ))} {[[120,60],[200,40],[160,100],[240,80],[100,130],[280,110]].map(([x,y],i) => ( <rect key={i} x={x-1} y={y-1} width={2} height={2} fill={color} opacity={0.5}/> ))} <path d="M60,200 L320,0" stroke={color} strokeWidth={0.5} strokeOpacity={0.12}/> </svg> ); }
function HorrorArt({ color }) { return ( <svg viewBox="0 0 380 200" preserveAspectRatio="xMidYMid slice" style={{ width:"100%", height:"100%" }}> <path d="M0,200 L60,80 L120,140 L160,20 L200,100 L240,40 L280,120 L320,30 L380,200Z" fill={color} opacity={0.06}/> <path d="M0,200 L80,100 L140,160 L190,50 L230,130 L280,60 L380,200Z" fill={color} opacity={0.04}/> {[0,1,2,3,4,5].map(i => ( <line key={i} x1={i*76} y1={0} x2={380-i*40} y2={200} stroke={color} strokeWidth={0.6} strokeOpacity={0.08+i*0.02}/> ))} <circle cx={190} cy={100} r={40} fill="none" stroke={color} strokeWidth={0.8} strokeOpacity={0.15}/> <circle cx={190} cy={100} r={20} fill="none" stroke={color} strokeWidth={0.5} strokeOpacity={0.1}/> </svg> ); }
function RomanceArt({ color }) { return ( <svg viewBox="0 0 380 200" preserveAspectRatio="xMidYMid slice" style={{ width:"100%", height:"100%" }}> {[[130,90,100,70],[250,110,90,65],[190,60,80,55],[160,140,70,50],[210,140,75,52]].map(([cx,cy,rx,ry],i) => ( <ellipse key={i} cx={cx} cy={cy} rx={rx} ry={ry} fill={color} opacity={0.06+i*0.02} transform={`rotate(${i*24},${cx},${cy})`}/> ))} {[[130,90,100,70],[250,110,90,65],[190,60,80,55]].map(([cx,cy,rx,ry],i) => ( <ellipse key={i} cx={cx} cy={cy} rx={rx} ry={ry} fill="none" stroke={color} strokeWidth={0.8} strokeOpacity={0.18} transform={`rotate(${i*24},${cx},${cy})`}/> ))} <path d="M190,80 C180,65 155,65 155,85 C155,105 190,120 190,120 C190,120 225,105 225,85 C225,65 200,65 190,80Z" fill={color} opacity={0.25}/> </svg> ); }
function MentalHealthArt({ color }) { return ( <svg viewBox="0 0 380 200" preserveAspectRatio="xMidYMid slice" style={{ width:"100%", height:"100%" }}> {[90,75,60,45,30,15].map((r,i) => ( <circle key={i} cx={190} cy={100} r={r} fill="none" stroke={color} strokeWidth={0.8} strokeOpacity={0.1+i*0.04} strokeDasharray={i%2===0?"4 4":"none"}/> ))} {[0,30,60,90,120,150].map((angle,i) => { const rad = angle * Math.PI / 180; return ( <line key={i} x1={190+90*Math.cos(rad)} y1={100+90*Math.sin(rad)} x2={190+110*Math.cos(rad)} y2={100+110*Math.sin(rad)} stroke={color} strokeWidth={1} strokeOpacity={0.2}/> ); })} <circle cx={190} cy={100} r={8} fill={color} opacity={0.3}/> <circle cx={190} cy={100} r={4} fill={color} opacity={0.5}/> </svg> ); }
function ComedyArt({ color }) { return ( <svg viewBox="0 0 380 200" preserveAspectRatio="xMidYMid slice" style={{ width:"100%", height:"100%" }}> {[[60,40,14],[120,80,10],[180,30,18],[240,70,12],[300,45,16],[80,150,8],[160,130,14],[220,160,10],[310,140,12],[350,80,6]].map(([x,y,r],i) => ( <circle key={i} cx={x} cy={y} r={r} fill={color} opacity={0.06+i*0.015}/> ))} {[[60,40,14],[180,30,18],[300,45,16],[160,130,14]].map(([x,y,r],i) => ( <circle key={i} cx={x} cy={y} r={r} fill="none" stroke={color} strokeWidth={0.8} strokeOpacity={0.25}/> ))} <path d="M90,100 Q150,60 190,80 Q230,100 290,70" fill="none" stroke={color} strokeWidth={1.5} strokeOpacity={0.22} strokeLinecap="round"/> </svg> ); }
function SelfDiscoveryArt({ color }) { return ( <svg viewBox="0 0 380 200" preserveAspectRatio="xMidYMid slice" style={{ width:"100%", height:"100%" }}> {Array.from({ length:5 }, (_,i) => { const r = 20+i*28, angle = i*137.5*Math.PI/180; return ( <g key={i}> <circle cx={190+r*Math.cos(angle)} cy={100+r*Math.sin(angle)*0.6} r={8-i} fill={color} opacity={0.08+i*0.04}/> </g> ); })} {[50,75,100,125,150].map((r,i) => ( <path key={i} d={`M ${190+r} ${100} A ${r} ${r*0.7} 0 0 1 ${190-r} ${100}`} fill="none" stroke={color} strokeWidth={0.7} strokeOpacity={0.1+i*0.04}/> ))} <circle cx={190} cy={100} r={5} fill={color} opacity={0.5}/> {[0,72,144,216,288].map((a,i) => { const rad = a*Math.PI/180; return <line key={i} x1={190} y1={100} x2={190+55*Math.cos(rad)} y2={100+38*Math.sin(rad)} stroke={color} strokeWidth={0.6} strokeOpacity={0.18}/>; })} </svg> ); }

function ThemeArt({ theme, height = 180 }) {
  const artMap = {
    "real-journey":   <RealJourneyArt   color={theme.color}/>,
    "motivational":   <MotivationalArt  color={theme.color}/>,
    "fiction":        <FictionArt       color={theme.color}/>,
    "horror":         <HorrorArt        color={theme.color}/>,
    "romance":        <RomanceArt       color={theme.color}/>,
    "mental-health":  <MentalHealthArt  color={theme.color}/>,
    "comedy":         <ComedyArt        color={theme.color}/>,
    "self-discovery": <SelfDiscoveryArt color={theme.color}/>,
  };
  return (
    <div style={{ height, width:"100%", background:theme.light, overflow:"hidden", position:"relative", flexShrink:0 }}>
      {artMap[theme.id] || <MentalHealthArt color={theme.color}/>}
    </div>
  );
}

/* ══════════════════════════════════════════════
   HOOKS (unchanged)
══════════════════════════════════════════════ */

function useReactions(storyId, enabled = false, initialLikes = 0, initialDislikes = 0) {
  const [likes, setLikes]       = useState(initialLikes);
  const [dislikes, setDislikes] = useState(initialDislikes);
  const [myVote, setMyVote]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const uid = auth.currentUser?.uid;

  useEffect(() => {
    if (!enabled) { setLikes(initialLikes); setDislikes(initialDislikes); }
  }, [initialLikes, initialDislikes, enabled]);

  useEffect(() => {
    if (!enabled || !storyId) return;
    let unsub = null, cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      unsub = onSnapshot(
        doc(db, "stories", storyId),
        { includeMetadataChanges: false },
        snap => {
          if (cancelled || !snap.exists()) return;
          setLikes(snap.data().likes || 0);
          setDislikes(snap.data().dislikes || 0);
        },
        err => { if (!err.message?.includes("INTERNAL ASSERTION FAILED")) console.error(err); }
      );
    }, 50);
    return () => { cancelled = true; clearTimeout(t); unsub?.(); };
  }, [enabled, storyId]);

  useEffect(() => {
    if (!uid || !storyId) return;
    getDoc(doc(db, "storyReactions", storyId, "votes", uid))
      .then(s => { if (s.exists()) setMyVote(s.data().vote); })
      .catch(() => {});
  }, [storyId, uid]);

  async function vote(type) {
    if (!uid || loading) return;
    setLoading(true);
    const pv = myVote, pl = likes, pd = dislikes;
    const vRef = doc(db, "storyReactions", storyId, "votes", uid);
    const sRef = doc(db, "stories", storyId);
    try {
      if (pv === type) {
        setMyVote(null);
        if (!enabled) {
          if (type === "like")    setLikes(l => Math.max(0, l - 1));
          if (type === "dislike") setDislikes(d => Math.max(0, d - 1));
        }
        await deleteDoc(vRef);
        await updateDoc(sRef, { [type === "like" ? "likes" : "dislikes"]: increment(-1) });
      } else {
        setMyVote(type);
        if (!enabled) {
          if (pv === "like")    setLikes(l => Math.max(0, l - 1));
          if (pv === "dislike") setDislikes(d => Math.max(0, d - 1));
          if (type === "like")    setLikes(l => l + 1);
          if (type === "dislike") setDislikes(d => d + 1);
        }
        if (pv) await updateDoc(sRef, { [pv === "like" ? "likes" : "dislikes"]: increment(-1) });
        await setDoc(vRef, { vote: type });
        await updateDoc(sRef, { [type === "like" ? "likes" : "dislikes"]: increment(1) });
      }
    } catch {
      setMyVote(pv); setLikes(pl); setDislikes(pd);
    } finally {
      setLoading(false);
    }
  }
  return { likes, dislikes, myVote, vote, loggedIn: !!uid };
}

function useFollow(authorId) {
  const [following, setFollowing] = useState(false);
  const [fCount, setFCount]       = useState(0);
  const [loading, setLoading]     = useState(false);
  const uid    = auth.currentUser?.uid;
  const isSelf = !!(uid && authorId && uid === authorId);

  useEffect(() => {
    if (!authorId) return;
    let unsub = null, cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      unsub = onSnapshot(
        doc(db, "users", authorId),
        { includeMetadataChanges: false },
        snap => { if (!cancelled && snap.exists()) setFCount(snap.data().followerCount || 0); },
        err  => { if (!err.message?.includes("INTERNAL ASSERTION FAILED")) console.error(err); }
      );
    }, 50);
    return () => { cancelled = true; clearTimeout(t); unsub?.(); };
  }, [authorId]);

  useEffect(() => {
    if (!uid || !authorId || isSelf) return;
    setFollowing(false);
    getDoc(doc(db, "follows", `${uid}_${authorId}`))
      .then(s => setFollowing(s.exists()))
      .catch(() => {});
  }, [uid, authorId, isSelf]);

  function syncLocal(isF) {
    try {
      const a = JSON.parse(localStorage.getItem("hh_following") || "[]");
      const s = new Set(a);
      isF ? s.add(authorId) : s.delete(authorId);
      localStorage.setItem("hh_following", JSON.stringify([...s]));
    } catch {}
  }

  async function toggleFollow() {
    if (!uid) { alert("Please log in to follow authors."); return; }
    if (isSelf || loading) return;
    setLoading(true);
    const was = following;
    setFollowing(!was); setFCount(c => was ? Math.max(0, c - 1) : c + 1);
    const fRef = doc(db, "follows", `${uid}_${authorId}`);
    const aRef = doc(db, "users", authorId);
    try {
      if (was) {
        await deleteDoc(fRef);
        await updateDoc(aRef, { followerCount: increment(-1) });
        syncLocal(false);
      } else {
        await setDoc(fRef, { followerId: uid, authorId, createdAt: serverTimestamp() });
        await updateDoc(aRef, { followerCount: increment(1) });
        syncLocal(true);
      }
    } catch {
      setFollowing(was); setFCount(c => was ? c + 1 : Math.max(0, c - 1));
    } finally {
      setLoading(false);
    }
  }
  return { following, fCount, toggleFollow, isSelf, loggedIn: !!uid };
}

function useBookmarks() {
  const [bookmarks, setBookmarks] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("hh_bookmarks") || "[]")); }
    catch { return new Set(); }
  });
  const toggle = useCallback((id) => {
    setBookmarks(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      try { localStorage.setItem("hh_bookmarks", JSON.stringify([...n])); } catch {}
      return n;
    });
  }, []);
  return { bookmarks, toggle };
}

/* ══════════════════════════════════════════════
   ELITE MOBILE CSS v10.2 — Production-ready
   
   C1: .mj-root is display:flex, min-height:0, NO overflow.
       Parent .hh-screen handles all scrolling.
       .mj-nav-strip is position:sticky.
       .mj-feed-area flows naturally, no overflow-y.
   C4: .mj-search-overlay is position:fixed.
   C5: No @import — fonts loaded separately via <link>.
   D2: Like button visually larger than secondary actions.
   D3: Compact art responsive on small screens.
   D5: Quote truncation with text-overflow fallback.
══════════════════════════════════════════════ */
const ELITE_JOURNAL_CSS = `
  :root {
    --mj-spring:  cubic-bezier(0.25, 1, 0.4, 1);
    --mj-bounce:  cubic-bezier(0.34, 1.56, 0.64, 1);
    --mj-bg:      var(--cloud, #F4F7F9);
    --mj-card:    var(--card, #FFFFFF);
    --mj-brand:   var(--brand, #5A6993);
    --mj-ink:     var(--ink, #111111);
    --mj-muted:   var(--muted, #6B7280);
    --mj-sand:    var(--sand-light, #E5E7EB);
    --mj-serif:   'Cormorant Garamond', Georgia, serif;
    --mj-sans:    'Plus Jakarta Sans', system-ui, sans-serif;
  }
  body.dark-theme {
    --mj-bg:   #151820;
    --mj-card: #1e2230;
    --mj-ink:  #F9FAFB;
    --mj-muted:#9CA3AF;
    --mj-sand: rgba(255,255,255,0.08);
  }

  /* C1 FIX: NO height constraint, NO overflow.
     Parent .hh-screen (.hh-explore-content) is the sole scroll container.
     .mj-root just stacks nav + content vertically. */
  .mj-root {
    font-family: var(--mj-sans);
    display: flex;
    flex-direction: column;
    min-height: 0;
    position: relative;
  }

  /* ═══ STICKY NAV ═══ */
  .mj-nav-strip {
    position: sticky;
    top: 0;
    z-index: 30;
    flex-shrink: 0;
    background: var(--mj-card);
    border-bottom: 1px solid var(--mj-sand);
    padding: 8px 0 0;
  }
  .mj-nav-row {
    display: flex; align-items: center; padding: 0 12px 8px; gap: 8px;
  }
  .mj-nav-tabs {
    display: flex; gap: 2px; flex: 1;
    overflow-x: auto; scrollbar-width: none; -webkit-overflow-scrolling: touch;
  }
  .mj-nav-tabs::-webkit-scrollbar { display: none; }
  .mj-nav-tab {
    flex-shrink: 0; padding: 6px 12px; border-radius: 8px;
    font-family: var(--mj-sans); font-size: 0.78rem; font-weight: 600;
    color: var(--mj-muted); background: none; border: none; cursor: pointer;
    transition: all 0.15s ease; -webkit-tap-highlight-color: transparent; white-space: nowrap;
  }
  .mj-nav-tab:active { transform: scale(0.95); }
  .mj-nav-tab.active { color: var(--mj-ink); background: var(--mj-bg); font-weight: 800; }
  .mj-nav-actions { display: flex; gap: 4px; flex-shrink: 0; align-items: center; }
  .mj-nav-icon-btn {
    width: 32px; height: 32px; border-radius: 10px;
    border: 1px solid var(--mj-sand); background: var(--mj-bg); color: var(--mj-muted);
    display: flex; align-items: center; justify-content: center; cursor: pointer;
    transition: all 0.15s ease; -webkit-tap-highlight-color: transparent;
  }
  .mj-nav-icon-btn:active { transform: scale(0.88); }
  .mj-nav-write { background: var(--mj-ink); color: var(--mj-card); border-color: var(--mj-ink); }

  .mj-theme-strip {
    display: flex; gap: 4px; padding: 0 12px 8px;
    overflow-x: auto; scrollbar-width: none; -webkit-overflow-scrolling: touch;
  }
  .mj-theme-strip::-webkit-scrollbar { display: none; }
  .mj-theme-pill {
    flex-shrink: 0; padding: 4px 10px; border-radius: 99px;
    font-size: 0.78rem; font-weight: 600; background: none;
    border: 1px solid transparent; color: var(--mj-muted); cursor: pointer;
    transition: all 0.15s ease; -webkit-tap-highlight-color: transparent; font-family: var(--mj-sans);
  }
  .mj-theme-pill:active { transform: scale(0.92); }
  .mj-theme-pill.active {
    background: var(--tp-light, var(--mj-bg)); color: var(--tp-color, var(--mj-ink));
    border-color: var(--tp-color, var(--mj-sand)); font-weight: 800;
  }

  /* C4 FIX: position:fixed escapes all scroll containers */
  .mj-search-overlay {
    position: fixed; top: 0; left: 0; right: 0; z-index: 200;
    background: var(--mj-card);
    animation: mjSlideDown 0.2s ease;
    padding: env(safe-area-inset-top, 12px) 12px 10px;
    border-bottom: 1px solid var(--mj-sand);
    box-shadow: 0 4px 16px rgba(0,0,0,0.08);
  }
  @keyframes mjSlideDown { from { transform: translateY(-100%); } to { transform: translateY(0); } }
  .mj-search-bar {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 14px; background: var(--mj-bg); border-radius: 14px; border: 1.5px solid var(--mj-sand);
  }
  .mj-search-overlay-input {
    flex: 1; border: none; background: none; outline: none;
    font-family: var(--mj-sans); font-size: 0.9rem; color: var(--mj-ink);
  }
  .mj-search-overlay-input::placeholder { color: var(--mj-muted); }
  .mj-search-close {
    background: none; border: none; color: var(--mj-brand);
    font-family: var(--mj-sans); font-size: 0.85rem; font-weight: 700; cursor: pointer; flex-shrink: 0;
  }

  /* ═══ SHARE SHEET ═══ */
  .mj-share-overlay {
    position: fixed; inset: 0; z-index: 300;
    background: rgba(0,0,0,0.45); display: flex; align-items: flex-end;
    animation: mjFadeIn 0.2s ease;
  }
  @keyframes mjFadeIn { from { opacity: 0; } to { opacity: 1; } }
  .mj-share-sheet {
    width: 100%; background: var(--mj-card);
    border-top-left-radius: 24px; border-top-right-radius: 24px;
    padding: 12px 20px calc(20px + env(safe-area-inset-bottom, 0));
    animation: mjSheetUp 0.3s var(--mj-spring);
    display: flex; flex-direction: column; align-items: center;
  }
  @keyframes mjSheetUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
  .mj-share-handle { width: 36px; height: 4px; border-radius: 2px; background: var(--mj-sand); margin-bottom: 16px; }
  .mj-share-option {
    width: 100%; display: flex; align-items: center; gap: 12px;
    padding: 14px 16px; border-radius: 14px;
    background: var(--mj-bg); border: 1px solid var(--mj-sand);
    font-family: var(--mj-sans); font-size: 0.88rem; font-weight: 600;
    color: var(--mj-ink); cursor: pointer; transition: all 0.15s; -webkit-tap-highlight-color: transparent;
  }
  .mj-share-option:active { transform: scale(0.97); }

  /* ═══ ENGAGEMENT BUTTONS (D2: visual hierarchy, #20: 44px tap targets) ═══ */
  .mj-engage-btn {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 10px 14px; border-radius: 99px;
    min-height: 44px; box-sizing: border-box;
    border: 1px solid var(--mj-sand); background: var(--mj-bg); color: var(--mj-muted);
    font-family: var(--mj-sans); font-size: 0.76rem; font-weight: 700;
    cursor: pointer; transition: all 0.15s var(--mj-spring); -webkit-tap-highlight-color: transparent;
  }
  .mj-engage-btn:active { transform: scale(0.88); }
  /* D2: secondary actions are visually quieter */
  .mj-engage-secondary { opacity: 0.7; }
  .mj-engage-secondary:hover { opacity: 1; }
  .mj-engage-liked { background: rgba(239,68,68,0.08); color: #EF4444; border-color: rgba(239,68,68,0.2); opacity: 1; }
  .mj-engage-pop svg { animation: mjHeartPop 0.4s var(--mj-bounce); }
  @keyframes mjHeartPop { 0% { transform: scale(1); } 40% { transform: scale(1.5); } 100% { transform: scale(1); } }
  .mj-engage-saved { background: rgba(16,185,129,0.08); color: #10B981; border-color: rgba(16,185,129,0.2); opacity: 1; }
  .mj-engage-saved svg { animation: mjBookmarkPop 0.35s var(--mj-bounce); }
  @keyframes mjBookmarkPop { 0% { transform: scale(1) rotate(0); } 50% { transform: scale(1.3) rotate(12deg); } 100% { transform: scale(1) rotate(0); } }
  .mj-engage-danger { color: #EF4444; }
  .mj-engage-light { background: rgba(255,255,255,0.12); border-color: rgba(255,255,255,0.2); color: rgba(255,255,255,0.8); }
  .mj-engage-light:active { background: rgba(255,255,255,0.2); }
  .mj-engage-light.mj-engage-liked { background: rgba(239,68,68,0.25); color: #ff8a8a; border-color: rgba(239,68,68,0.3); }
  .mj-engage-light.mj-engage-saved { background: rgba(16,185,129,0.2); color: #6ee7b7; border-color: rgba(16,185,129,0.3); }

  .mj-card-engage {
    display: flex; align-items: center; gap: 6px;
    padding-top: 8px; margin-top: 4px; border-top: 1px solid var(--mj-sand);
  }
  .mj-hero-engage {
    display: flex; align-items: center; gap: 6px;
    border-top: 1px solid rgba(255,255,255,0.12); margin-top: 12px; padding-top: 10px;
  }

  .mj-fab {
    position: fixed;
    bottom: calc(env(safe-area-inset-bottom, 0px) + 28px);
    right: 20px; z-index: 60;
    width: 54px; height: 54px; border-radius: 50%;
    background: var(--mj-ink); color: var(--mj-card); border: none;
    display: flex; align-items: center; justify-content: center; font-size: 1.3rem;
    box-shadow: 0 6px 24px rgba(0,0,0,0.25); cursor: pointer;
    transition: transform 0.2s var(--mj-bounce); -webkit-tap-highlight-color: transparent;
  }
  body.dark-theme .mj-fab { background: #fff; color: #111; }
  .mj-fab:active { transform: scale(0.88); }

  /* C1 FIX: feed area has NO overflow — parent scrolls */
  .mj-feed-area {
    padding: 10px 14px 100px;
    display: flex; flex-direction: column; gap: 14px;
  }

  /* ═══ CARDS ═══ */
  .mj-card {
    background: var(--mj-card); border-radius: 22px; border: 1px solid var(--mj-sand);
    overflow: hidden; box-shadow: 0 2px 12px rgba(0,0,0,0.04);
    cursor: pointer; -webkit-tap-highlight-color: transparent;
    display: flex; flex-direction: column; transition: transform 0.18s var(--mj-spring);
  }
  .mj-card:active { transform: scale(0.975); }
  .mj-card-cover { height: 140px; position: relative; overflow: hidden; flex-shrink: 0; }
  .mj-card-badge {
    position: absolute; top: 10px; right: 10px; z-index: 2;
    background: rgba(255,255,255,0.92); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
    padding: 4px 10px; border-radius: 99px; font-size: 0.68rem; font-weight: 800;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  }
  .mj-card-body { padding: 12px 14px; display: flex; flex-direction: column; gap: 5px; flex: 1; }
  .mj-card-author-row { display: flex; align-items: center; gap: 8px; margin-bottom: 2px; }
  .mj-card-av {
    width: 30px; height: 30px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-weight: 800; font-size: 0.78rem; flex-shrink: 0;
  }
  .mj-card-author-info { flex: 1; min-width: 0; }
  .mj-card-author-name {
    font-size: 0.8rem; font-weight: 700; color: var(--mj-ink);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .mj-card-author-date { font-size: 0.68rem; color: var(--mj-muted); margin-top: 1px; }
  .mj-card-title {
    font-family: var(--mj-serif); font-size: 1.3rem; font-weight: 700; line-height: 1.2;
    color: var(--mj-ink); margin: 0;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
  }
  .mj-card-preview {
    font-size: 0.82rem; color: var(--mj-muted); line-height: 1.5; margin: 0;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
  }
  .mj-card-lastline {
    font-size: 0.76rem; color: var(--mj-muted); font-style: italic; opacity: 0.6; line-height: 1.4; margin: 0;
    display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden;
    text-overflow: ellipsis; border-left: 2px solid var(--mj-sand); padding-left: 8px;
  }

  /* ═══ COMPACT CARD (D3: responsive art width) ═══ */
  .mj-compact {
    display: flex; background: var(--mj-card); border-radius: 18px; border: 1px solid var(--mj-sand);
    overflow: hidden; cursor: pointer; -webkit-tap-highlight-color: transparent;
    transition: transform 0.18s var(--mj-spring); box-shadow: 0 1px 8px rgba(0,0,0,0.03);
  }
  .mj-compact:active { transform: scale(0.975); }
  .mj-compact-art { width: 100px; flex-shrink: 0; overflow: hidden; }
  @media (max-width: 380px) { .mj-compact-art { width: 80px; } }
  .mj-compact-body { flex: 1; padding: 10px 12px; display: flex; flex-direction: column; gap: 3px; min-width: 0; }
  .mj-compact-title {
    font-family: var(--mj-serif); font-size: 1.05rem; font-weight: 700; color: var(--mj-ink);
    line-height: 1.2; margin: 0;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
  }
  .mj-compact-author { font-size: 0.7rem; color: var(--mj-muted); }

  /* ═══ TEXT CARD (D5: quote truncation fix) ═══ */
  .mj-text-card {
    background: var(--mj-card); border-radius: 20px; border: 1px solid var(--mj-sand);
    padding: 14px 16px; cursor: pointer; -webkit-tap-highlight-color: transparent;
    transition: transform 0.18s var(--mj-spring); box-shadow: 0 1px 8px rgba(0,0,0,0.03);
  }
  .mj-text-card:active { transform: scale(0.975); }
  .mj-text-header { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
  .mj-text-av {
    width: 28px; height: 28px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-weight: 800; font-size: 0.75rem; flex-shrink: 0;
  }
  .mj-text-badge { padding: 3px 8px; border-radius: 8px; font-size: 0.65rem; font-weight: 700; flex-shrink: 0; }
  .mj-text-title {
    font-family: var(--mj-serif); font-size: 1.2rem; font-weight: 700;
    color: var(--mj-ink); line-height: 1.25; margin: 0 0 6px;
  }
  .mj-text-quote {
    font-family: var(--mj-serif); font-style: italic;
    font-size: 0.88rem; color: var(--mj-muted); line-height: 1.55;
    border-left: 3px solid var(--tc, var(--mj-brand));
    padding-left: 12px; margin: 0 0 8px;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
    overflow: hidden; text-overflow: ellipsis;
    word-break: break-word;
  }

  /* ═══ HERO CARD ═══ */
  .mj-hero-card {
    background: var(--mj-card); border-radius: 24px; border: 1px solid var(--mj-sand);
    overflow: hidden; box-shadow: 0 12px 36px rgba(0,0,0,0.1);
    position: relative; cursor: pointer; -webkit-tap-highlight-color: transparent;
    height: 360px; display: flex; flex-direction: column; justify-content: flex-end;
    transition: transform 0.2s var(--mj-spring); flex-shrink: 0;
  }
  .mj-hero-card:active { transform: scale(0.97); }
  .mj-hero-bg { position: absolute; inset: 0; z-index: 0; }
  .mj-hero-bg > div > svg { animation: mjSlowZoom 22s infinite alternate ease-in-out; }
  @keyframes mjSlowZoom { from { transform: scale(1); } to { transform: scale(1.12); } }
  .mj-hero-overlay {
    position: absolute; inset: 0; z-index: 1;
    background: linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.2) 55%, transparent 100%);
  }
  .mj-hero-content { position: relative; z-index: 2; padding: 20px; color: #fff; }
  .mj-hero-tag {
    background: rgba(255,255,255,0.15); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
    padding: 4px 10px; border-radius: 99px; font-size: 0.7rem; font-weight: 800;
    display: inline-flex; align-items: center; gap: 4px; margin-bottom: 8px;
  }
  .mj-hero-title {
    font-family: var(--mj-serif); font-size: 1.85rem; font-weight: 700; line-height: 1.12;
    margin: 0 0 12px; text-shadow: 0 2px 12px rgba(0,0,0,0.5);
    display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
  }
  .mj-hero-meta { display: flex; align-items: center; gap: 10px; }
  .mj-hero-avatar {
    width: 34px; height: 34px; border-radius: 50%;
    background: rgba(255,255,255,0.2); display: flex; align-items: center; justify-content: center;
    font-weight: 800; font-size: 0.85rem; border: 2px solid rgba(255,255,255,0.4); flex-shrink: 0;
  }
  .mj-hero-stats { display: flex; align-items: center; gap: 8px; font-size: 0.76rem; color: rgba(255,255,255,0.7); font-weight: 600; }

  /* ═══ STATES ═══ */
  .mj-empty { text-align: center; padding: 48px 20px; }
  .mj-empty-icon { font-size: 3rem; margin-bottom: 16px; animation: mjFloat 4s ease-in-out infinite; display: block; }
  @keyframes mjFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
  .mj-empty-write {
    margin-top: 16px; padding: 12px 24px;
    background: var(--mj-ink); color: var(--mj-card); border: none; border-radius: 14px;
    font-family: var(--mj-sans); font-size: 0.88rem; font-weight: 700; cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .mj-empty-write:active { transform: scale(0.95); }

  @keyframes mjCardIn { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
  .mj-card-enter { animation: mjCardIn 0.35s var(--mj-spring) both; animation-delay: calc(var(--idx, 0) * 0.03s); }
  @keyframes mjShimmer { 0% { background-position: -500px 0; } 100% { background-position: 500px 0; } }
  .mj-skel-base {
    background: linear-gradient(90deg, var(--mj-sand) 25%, rgba(255,255,255,0.55) 50%, var(--mj-sand) 75%);
    background-size: 1000px 100%; animation: mjShimmer 1.6s ease infinite; border-radius: 6px;
  }
  .mj-skel-card { background: var(--mj-card); border-radius: 22px; border: 1px solid var(--mj-sand); overflow: hidden; flex-shrink: 0; }
  .mj-sentinel { height: 1px; margin-top: -1px; }
  .mj-load-more { display: flex; align-items: center; justify-content: center; gap: 8px; padding: 20px; font-size: 0.82rem; color: var(--mj-muted); font-weight: 600; }
  @keyframes mjSpin { to { transform: rotate(360deg); } }
  .mj-spinner { width: 18px; height: 18px; border-radius: 50%; border: 2.5px solid var(--mj-sand); border-top-color: var(--mj-ink); animation: mjSpin 0.7s linear infinite; }

  /* ═══ MODALS (preserved from v9) ═══ */
  .mj-sheet-overlay { position: fixed; inset: 0; z-index: 100; background: var(--mj-bg); display: flex; flex-direction: column; animation: mjSlideUp 0.3s var(--mj-spring); overflow: hidden; }
  @keyframes mjSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
  .mj-read-header { display: flex; align-items: center; justify-content: space-between; padding: env(safe-area-inset-top, 20px) 16px 12px; background: rgba(255,255,255,0.92); backdrop-filter: blur(32px) saturate(160%); -webkit-backdrop-filter: blur(32px) saturate(160%); border-bottom: 1px solid var(--mj-sand); position: absolute; top: 0; left: 0; right: 0; z-index: 50; transition: transform 0.38s var(--mj-spring), opacity 0.3s ease; }
  body.dark-theme .mj-read-header { background: rgba(30,34,48,0.92); }
  .mj-read-header.hidden { transform: translateY(-100%); opacity: 0; pointer-events: none; }
  .mj-read-progress-bar { position: absolute; bottom: 0; left: 0; height: 2.5px; background: var(--theme-color, var(--mj-brand)); transition: width 0.12s linear; border-radius: 0 2px 2px 0; }
  .mj-read-content { flex: 1; overflow-y: auto; -webkit-overflow-scrolling: touch; overscroll-behavior-y: contain; padding-bottom: calc(72px + env(safe-area-inset-bottom, 0px)); }
  .mj-read-art { position: relative; overflow: hidden; flex-shrink: 0; }
  .mj-read-body { padding: 28px 22px 20px; }
  .mj-read-title { font-family: var(--mj-serif); font-size: 2.3rem; font-weight: 700; line-height: 1.13; color: var(--mj-ink); margin: 0 0 22px; letter-spacing: -0.01em; animation: mjTextUp 0.55s var(--mj-spring) 0.08s both; }
  @keyframes mjTextUp { from { transform: translateY(14px); } to { transform: translateY(0); } }
  .mj-read-author { display: flex; align-items: center; gap: 12px; margin-bottom: 32px; padding-bottom: 24px; border-bottom: 1px solid var(--mj-sand); animation: mjTextUp 0.55s var(--mj-spring) 0.16s both; }
  .mj-read-prose { font-family: var(--mj-serif); font-size: 1.22rem; line-height: 1.9; color: var(--mj-ink); letter-spacing: 0.008em; }
  .mj-read-prose p { margin: 0 0 1.5em; }
  .mj-read-prose div { margin: 0 0 1.2em; }
  .mj-read-prose blockquote { border-left: 3px solid var(--theme-color, var(--mj-brand)); padding-left: 18px; margin: 26px 0; font-style: italic; color: var(--mj-muted); font-size: 1.3rem; }
  .mj-read-prose > * { animation: mjTextUpNoOpacity 0.55s var(--mj-spring) both; animation-delay: calc(0.08s + (var(--idx, 0) * 0.08s)); }
  @keyframes mjTextUpNoOpacity { from { transform: translateY(12px); } to { transform: translateY(0); } }
  .mj-read-prose > *:nth-child(1) { --idx: 1; } .mj-read-prose > *:nth-child(2) { --idx: 2; } .mj-read-prose > *:nth-child(3) { --idx: 3; } .mj-read-prose > *:nth-child(n+4) { --idx: 4; }
  .mj-reflection { background: linear-gradient(135deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.2) 100%); border-left: 4px solid var(--theme-color, var(--mj-brand)); padding: 18px 20px; margin: 24px 0 32px; border-radius: 16px; font-family: var(--mj-serif); font-size: 1.05rem; line-height: 1.6; color: var(--mj-ink); font-style: italic; box-shadow: 0 2px 8px rgba(0,0,0,0.04); }
  .mj-reflection-label { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.1em; font-weight: 800; color: var(--theme-color, var(--mj-brand)); margin-bottom: 8px; font-style: normal; }
  .mj-comments-area { margin-top: 44px; padding-top: 32px; border-top: 1px solid var(--mj-sand); animation: mjTextUp 0.55s var(--mj-spring) 0.6s both; }
  .mj-comment-input-wrap { position: sticky; bottom: 0; padding: 14px 20px calc(14px + env(safe-area-inset-bottom, 0px)); background: rgba(255,255,255,0.92); backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px); border-top: 1px solid var(--mj-sand); display: flex; gap: 10px; align-items: flex-end; margin: 0 -22px; }
  body.dark-theme .mj-comment-input-wrap { background: rgba(30,34,48,0.92); }
  .mj-edit-header { display: flex; align-items: center; justify-content: space-between; padding: env(safe-area-inset-top, 20px) 16px 12px; background: var(--mj-card); border-bottom: 1px solid var(--mj-sand); flex-shrink: 0; }
  .mj-edit-title-input { width: 100%; padding: 22px 22px 10px; border: none; background: transparent; font-family: var(--mj-serif); font-size: 2.1rem; font-weight: 700; color: var(--mj-ink); outline: none; line-height: 1.12; box-sizing: border-box; }
  .mj-edit-title-input::placeholder { color: var(--mj-muted); opacity: 0.38; }
  .mj-editor-toolbar { display: flex; gap: 6px; padding: 10px 18px; overflow-x: auto; scrollbar-width: none; background: rgba(255,255,255,0.96); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); border-bottom: 1px solid var(--mj-sand); position: sticky; top: 0; z-index: 10; }
  body.dark-theme .mj-editor-toolbar { background: rgba(30,34,48,0.96); }
  .mj-editor-toolbar::-webkit-scrollbar { display: none; }
  .mj-tool-btn { flex-shrink: 0; width: 42px; height: 42px; border-radius: 10px; border: none; background: var(--mj-bg); color: var(--mj-ink); font-weight: 700; font-size: 1.05rem; display: flex; align-items: center; justify-content: center; cursor: pointer; -webkit-tap-highlight-color: transparent; transition: background 0.15s, color 0.15s; }
  .mj-tool-btn.active { background: var(--mj-ink); color: var(--mj-card); }
  .mj-edit-container { flex: 1; overflow-y: auto; position: relative; transition: background 0.5s ease; }
  .mj-chip-row { display: flex; gap: 8px; overflow-x: auto; scrollbar-width: none; scroll-snap-type: x mandatory; padding-bottom: 4px; }
  .mj-chip-row::-webkit-scrollbar { display: none; }
  .mj-chip { scroll-snap-align: start; flex-shrink: 0; padding: 9px 18px; border-radius: 99px; font-weight: 700; font-size: 0.82rem; background: var(--mj-card); border: 1px solid var(--mj-sand); color: var(--mj-muted); cursor: pointer; transition: all 0.2s var(--mj-spring); box-shadow: 0 2px 6px rgba(0,0,0,0.03); -webkit-tap-highlight-color: transparent; font-family: var(--mj-sans); }
  .mj-chip:active { transform: scale(0.93); }
  .mj-chip.active { background: var(--chip-bg, var(--mj-ink)); color: #fff; border-color: var(--chip-bg, var(--mj-ink)); box-shadow: 0 4px 14px rgba(0,0,0,0.12); }
  .mj-btn-icon { display: flex; align-items: center; gap: 4px; padding: 7px 13px; border-radius: 99px; border: 1px solid var(--mj-sand); background: var(--mj-bg); color: var(--mj-muted); font-family: var(--mj-sans); font-size: 0.8rem; font-weight: 700; transition: transform 0.2s var(--mj-bounce), background 0.15s, color 0.15s; cursor: pointer; -webkit-tap-highlight-color: transparent; }
  .mj-btn-icon:active { transform: scale(0.88); }
  .mj-btn-icon.liked { background: rgba(239,68,68,0.1); color: #EF4444; border-color: rgba(239,68,68,0.2); }
  .mj-btn-icon.liked svg { animation: mjHeartPop 0.4s var(--mj-bounce); }
  .mj-btn-icon.saved { background: rgba(16,185,129,0.1); color: #10B981; border-color: rgba(16,185,129,0.2); }
`;

/* ══════════════════════════════════════════════
   SKELETON CARD (unchanged)
══════════════════════════════════════════════ */
function SkeletonCard() {
  return (
    <div className="mj-skel-card">
      <div className="mj-skel-base" style={{ height:148, borderRadius:0 }}/>
      <div style={{ padding:"12px 16px 14px", display:"flex", flexDirection:"column", gap:9 }}>
        <div style={{ display:"flex", alignItems:"center", gap:9 }}>
          <div className="mj-skel-base" style={{ width:32, height:32, borderRadius:"50%", flexShrink:0 }}/>
          <div style={{ flex:1, display:"flex", flexDirection:"column", gap:5 }}>
            <div className="mj-skel-base" style={{ width:"45%", height:10 }}/>
            <div className="mj-skel-base" style={{ width:"30%", height:9 }}/>
          </div>
        </div>
        <div className="mj-skel-base" style={{ width:"90%", height:20 }}/>
        <div className="mj-skel-base" style={{ width:"70%", height:20 }}/>
        <div className="mj-skel-base" style={{ width:"100%", height:11 }}/>
        <div className="mj-skel-base" style={{ width:"82%", height:11 }}/>
        <div style={{ display:"flex", gap:7, marginTop:2 }}>
          <div className="mj-skel-base" style={{ width:56, height:24, borderRadius:99 }}/>
          <div className="mj-skel-base" style={{ width:52, height:24, borderRadius:99 }}/>
        </div>
        <div style={{ height:1, background:"var(--mj-sand)", margin:"2px 0" }}/>
        <div style={{ display:"flex", justifyContent:"flex-end" }}>
          <div className="mj-skel-base" style={{ width:36, height:30, borderRadius:99 }}/>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   COMMENTS SECTION (unchanged)
══════════════════════════════════════════════ */
function CommentsSection({ storyId }) {
  const [comments, setComments]       = useState([]);
  const [text, setText]               = useState("");
  const [loading, setLoading]         = useState(true);
  const [posting, setPosting]         = useState(false);
  const [displayName, setDisplayName] = useState("Anonymous");
  const uid = auth.currentUser?.uid;

  useEffect(() => {
    if (!uid) return;
    getDoc(doc(db, "users", uid))
      .then(s => {
        if (s.exists() && s.data().name) setDisplayName(s.data().name);
        else if (auth.currentUser?.displayName) setDisplayName(auth.currentUser.displayName);
      })
      .catch(() => {});
  }, [uid]);

  useEffect(() => {
    if (!storyId) return;
    let unsub = null, cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      if (cancelled) return;
      const q = query(
        collection(db, "stories", storyId, "comments"),
        orderBy("createdAt", "desc"),
        limit(30)
      );
      unsub = onSnapshot(q, { includeMetadataChanges: false },
        snap => { if (!cancelled) { setComments(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false); } },
        err  => { if (!err.message?.includes("INTERNAL ASSERTION FAILED")) console.error(err); setLoading(false); }
      );
    }, 50);
    return () => { cancelled = true; clearTimeout(t); unsub?.(); };
  }, [storyId]);

  async function postComment() {
    if (!uid) { alert("Please log in to comment."); return; }
    if (!text.trim() || posting) return;
    triggerHaptic();
    const fl = containsBannedWords(text);
    if (fl.length) { alert(`Flagged: "${fl[0]}"`); return; }
    setPosting(true);
    try {
      await addDoc(collection(db, "stories", storyId, "comments"), {
        text: text.trim(), authorName: displayName, authorId: uid, createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, "stories", storyId), { commentCount: increment(1) });
      setText("");
    } catch (e) { console.error(e); }
    finally { setPosting(false); }
  }

  async function deleteComment(cid, cAuthorId) {
    if (!uid || uid !== cAuthorId) return;
    if (!window.confirm("Delete this comment?")) return;
    triggerHaptic("heavy");
    try {
      await deleteDoc(doc(db, "stories", storyId, "comments", cid));
      await updateDoc(doc(db, "stories", storyId), { commentCount: increment(-1) });
    } catch (e) { console.error(e); }
  }

  return (
    <div className="mj-comments-area">
      <h4 style={{ fontFamily:"var(--mj-sans)", fontSize:"1.1rem", fontWeight:800, color:"var(--mj-ink)", marginBottom:20, marginTop:0 }}>
        Responses{!loading && comments.length > 0 &&
          <span style={{ color:"var(--mj-muted)", fontSize:"0.9rem", fontWeight:500, marginLeft:8 }}>({comments.length})</span>}
      </h4>

      {loading ? (
        <div style={{ color:"var(--mj-muted)", padding:"16px 0", fontStyle:"italic" }}>Loading…</div>
      ) : comments.length === 0 ? (
        <div style={{ color:"var(--mj-muted)", padding:"16px 0", fontStyle:"italic", fontSize:"0.9rem" }}>
          Be the first to respond.
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column" }}>
          {comments.map(c => (
            <div key={c.id} style={{ display:"flex", gap:14, marginBottom:18 }}>
              <div style={{
                width:38, height:38, borderRadius:"50%", background:"var(--mj-sand)",
                display:"flex", alignItems:"center", justifyContent:"center",
                fontWeight:800, fontSize:"0.95rem", color:"var(--mj-ink)", flexShrink:0,
              }}>
                {(c.authorName || "A")[0].toUpperCase()}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
                  <strong style={{ fontSize:"0.88rem", color:"var(--mj-ink)" }}>{c.authorName || "Anonymous"}</strong>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:"0.7rem", color:"var(--mj-muted)" }}>
                      {c.createdAt?.toDate?.().toLocaleDateString("en-IN", { day:"numeric", month:"short" })}
                    </span>
                    {uid && c.authorId === uid && (
                      <button onClick={() => deleteComment(c.id, c.authorId)}
                        style={{ background:"none", border:"none", color:"#EF4444", fontSize:"0.75rem", fontWeight:700, cursor:"pointer" }}>
                        Delete
                      </button>
                    )}
                  </div>
                </div>
                <div style={{
                  padding:"12px 16px", background:"var(--mj-bg)", borderRadius:18,
                  borderTopLeftRadius:4, fontSize:"0.9rem", color:"var(--mj-ink)", lineHeight:1.55,
                }}>
                  {c.text}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {uid ? (
        <div className="mj-comment-input-wrap">
          <textarea
            rows={1} value={text} onChange={e => setText(e.target.value)}
            placeholder="Share your thoughts…"
            style={{
              flex:1, padding:"12px 18px", borderRadius:22,
              border:"1.5px solid var(--mj-sand)", background:"var(--mj-card)",
              outline:"none", fontFamily:"var(--mj-sans)", fontSize:"0.93rem",
              resize:"none", maxHeight:90, color:"var(--mj-ink)",
            }}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); postComment(); } }}
          />
          <button onClick={postComment} disabled={!text.trim() || posting}
            style={{
              padding:"12px 20px", background:"var(--mj-ink)", color:"var(--mj-card)",
              border:"none", borderRadius:22, fontWeight:800, fontSize:"0.9rem", cursor:"pointer",
              opacity: (!text.trim() || posting) ? 0.45 : 1,
            }}>
            {posting ? "…" : "Post"}
          </button>
        </div>
      ) : (
        <div style={{
          padding:"14px", textAlign:"center", background:"var(--mj-sand)",
          borderRadius:18, color:"var(--mj-muted)", marginTop:20, fontWeight:600, fontSize:"0.85rem",
        }}>
          Sign in to leave a response.
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   STORY MODAL (unchanged)
══════════════════════════════════════════════ */
function StoryModal({ story, onClose, isAdmin, onDelete }) {
  const [scrollPct, setScrollPct]   = useState(0);
  const [showHeader, setShowHeader] = useState(true);
  const bodyRef     = useRef(null);
  const lastScrollY = useRef(0);
  const ticking     = useRef(false);

  const theme = STORY_THEMES.find(t => t.id === story.theme) || STORY_THEMES[0];
  const uid   = auth.currentUser?.uid;
  const words = countWords(story.content);
  const readingTime = Math.max(1, Math.ceil(words / 200));
  const sanitizedContent = useMemo(() => sanitize(story.content), [story.content]);
  const insight = useMemo(() => getStoryInsight(story.content), [story.content]);

  const { likes, myVote, vote, loggedIn: likeLoggedIn } =
    useReactions(story.id, true, story.likes || 0, story.dislikes || 0);
  const { following, fCount, toggleFollow, isSelf, loggedIn: followLoggedIn } =
    useFollow(story.authorId);

  const canDelete = isSelf || isAdmin;

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    const fn = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const fn = () => {
      if (ticking.current) return;
      ticking.current = true;
      requestAnimationFrame(() => {
        const { scrollTop, scrollHeight, clientHeight } = el;
        const pct = scrollHeight <= clientHeight ? 100
          : Math.round((scrollTop / (scrollHeight - clientHeight)) * 100);
        setScrollPct(pct);
        if (scrollTop > lastScrollY.current + 10 && scrollTop > 80) setShowHeader(false);
        else if (scrollTop < lastScrollY.current - 10 || scrollTop <= 80) setShowHeader(true);
        lastScrollY.current = scrollTop;
        ticking.current = false;
      });
    };
    el.addEventListener("scroll", fn, { passive: true });
    return () => el.removeEventListener("scroll", fn);
  }, []);

  function handleVote(type) {
    if (!likeLoggedIn) { alert("Please log in to vote."); return; }
    triggerHaptic(); vote(type);
  }

  async function handleDelete() {
    if (!window.confirm("Delete this story permanently?")) return;
    triggerHaptic("heavy");
    try {
      await deleteDoc(doc(db, "stories", story.id));
      StoryCache.remove(story.id);
      onDelete(story.id);
      onClose();
    } catch (e) { console.error(e); }
  }

  const pubDate = story.createdAt?.toDate
    ? story.createdAt.toDate().toLocaleDateString("en-IN", { day:"numeric", month:"long", year:"numeric" })
    : "";

  return createPortal(
    <div className="mj-sheet-overlay" style={{ "--theme-color": theme.color }}>
      <div className={`mj-read-header ${showHeader ? "" : "hidden"}`}>
        <button onClick={() => { triggerHaptic(); onClose(); }}
          style={{
            background:"var(--mj-card)", border:"1.5px solid var(--mj-sand)",
            width:38, height:38, borderRadius:"50%",
            display:"flex", alignItems:"center", justifyContent:"center",
            color:"var(--mj-ink)", fontWeight:800, fontSize:"1rem", cursor:"pointer",
          }}>✕</button>

        <div style={{ display:"flex", gap:8 }}>
          <button onClick={() => handleVote("like")}
            className={`mj-btn-icon ${myVote === "like" ? "liked" : ""}`}
            style={{ background: myVote === "like" ? theme.light : "var(--mj-card)" }}>
            <svg width="16" height="16" viewBox="0 0 24 24"
              fill={myVote === "like" ? "currentColor" : "none"}
              stroke="currentColor" strokeWidth="2.2">
              <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
            </svg>
            <span>{likes}</span>
          </button>
          {canDelete && (
            <button onClick={handleDelete} className="mj-btn-icon" style={{ color:"#EF4444" }}>🗑️</button>
          )}
        </div>
        <div className="mj-read-progress-bar" style={{ width:`${scrollPct}%` }}/>
      </div>

      <div className="mj-read-content" ref={bodyRef}
        style={{ paddingTop: showHeader ? 72 : 0, transition:"padding-top 0.35s var(--mj-spring)" }}>

        <div className="mj-read-art" style={{ height:200 }}>
          <ThemeArt theme={theme} height={200}/>
          <div style={{
            position:"absolute", bottom:0, left:0, right:0, height:56,
            background:"linear-gradient(to bottom, transparent, var(--mj-bg))",
          }}/>
        </div>

        <div className="mj-read-body">
          <div style={{
            fontSize:"0.68rem", fontWeight:800, textTransform:"uppercase",
            letterSpacing:"0.1em", color:theme.color, marginBottom:14,
          }}>
            {theme.emoji} {theme.label}
          </div>

          <h1 className="mj-read-title">{story.title}</h1>

          <div className="mj-read-author">
            <div style={{
              width:50, height:50, borderRadius:"50%",
              background:theme.light, color:theme.color, flexShrink:0,
              display:"flex", alignItems:"center", justifyContent:"center",
              fontWeight:800, fontSize:"1.25rem", border:`2px solid ${theme.color}30`,
            }}>
              {(story.authorName || "A")[0].toUpperCase()}
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:800, fontSize:"1.05rem", color:"var(--mj-ink)" }}>
                {story.authorName || "Anonymous"}
              </div>
              <div style={{ fontSize:"0.78rem", color:"var(--mj-muted)", marginTop:3, fontWeight:500 }}>
                {fCount > 0 ? `${fCount} follower${fCount !== 1 ? "s" : ""} · ` : ""}
                {readingTime} min read{pubDate ? ` · ${pubDate}` : ""}
              </div>
            </div>
            {!isSelf && story.authorId && (
              <button
                onClick={() => { if (!followLoggedIn) return; triggerHaptic(); toggleFollow(); }}
                style={{
                  padding:"9px 16px", borderRadius:99, fontWeight:700, fontSize:"0.83rem",
                  border: following ? `1.5px solid ${theme.color}` : "1.5px solid var(--mj-sand)",
                  background: following ? theme.light : "var(--mj-card)",
                  color: following ? theme.color : "var(--mj-ink)",
                  cursor:"pointer", transition:"all 0.2s", flexShrink:0,
                }}>
                {following ? "✓ Following" : "+ Follow"}
              </button>
            )}
          </div>

          <div className="mj-reflection">
            <div className="mj-reflection-label">✦ Reflection</div>
            <div>{insight}</div>
          </div>

          <div className="mj-read-prose" dangerouslySetInnerHTML={{ __html: sanitizedContent }}/>
          <CommentsSection storyId={story.id}/>
        </div>
      </div>
    </div>
  , document.body);
}

/* ══════════════════════════════════════════════
   WRITE STORY FORM (unchanged)
══════════════════════════════════════════════ */
function WriteStoryForm({ onSuccess, onCancel }) {
  const [title, setTitle]             = useState("");
  const [theme, setTheme]             = useState("");
  const [content, setContent]         = useState("");
  const [authorName, setAuthorName]   = useState("");
  const [nameLoading, setNameLoading] = useState(true);
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState("");

  const user = auth.currentUser;
  const selectedTheme = STORY_THEMES.find(t => t.id === theme);
  const wordCount = countWords(content);
  const nameOk = authorName.trim().length >= 2;
  const edRef = useRef(null);
  const [fmt, setFmt] = useState({ bold:false, italic:false, underline:false });

  useEffect(() => {
    const fn = e => { if (e.key === "Escape") { triggerHaptic(); onCancel(); } };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onCancel]);

  useEffect(() => {
    async function loadName() {
      if (!user) { setNameLoading(false); return; }
      try {
        const s = await getDoc(doc(db, "users", user.uid));
        if (s.exists() && s.data().name) setAuthorName(s.data().name);
        else if (user.displayName) setAuthorName(user.displayName);
      } catch {}
      finally { setNameLoading(false); }
    }
    loadName();
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [user]);

  const syncFmt = useCallback(() => {
    setFmt({
      bold:      document.queryCommandState("bold"),
      italic:    document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
    });
  }, []);

  function execFormat(cmd, value = null) {
    edRef.current?.focus();
    document.execCommand(cmd, false, value);
    setContent(edRef.current?.innerHTML || "");
    syncFmt();
    triggerHaptic();
  }
  function handleInput() {
    setContent(edRef.current?.innerHTML || "");
    syncFmt();
  }

  async function handleSubmit() {
    setError("");
    if (!nameOk)          { setError("Enter your display name (min 2 chars)."); return; }
    if (!title.trim())    { setError("Please add a title."); return; }
    if (!theme)           { setError("Select a category."); return; }
    if (wordCount < 50)   { setError("Story must be at least 50 words."); return; }
    if (wordCount > 1000) { setError("Story exceeds 1000 word limit."); return; }
    const flagged = containsBannedWords(content);
    if (flagged.length)   { setError(`Inappropriate language: "${flagged[0]}".`); return; }
    setSubmitting(true);
    triggerHaptic("heavy");
    try {
      await setDoc(doc(db, "users", user.uid), { name: authorName.trim() }, { merge: true });
      const docRef = await addDoc(collection(db, "stories"), {
        title: title.trim(), theme, content: sanitize(content),
        authorName: authorName.trim(), authorId: user.uid,
        wordCount, likes:0, dislikes:0, commentCount:0,
        createdAt: serverTimestamp(),
      });
      const optimisticStory = {
        id: docRef.id,
        title: title.trim(),
        theme,
        content: sanitize(content),
        authorName: authorName.trim(),
        authorId: user.uid,
        wordCount,
        likes: 0,
        dislikes: 0,
        commentCount: 0,
        createdAt: { toDate: () => new Date() },
      };
      StoryCache.prepend(optimisticStory);
      onSuccess(optimisticStory);
    } catch { setError("Failed to publish. Please try again."); setSubmitting(false); }
  }

  return createPortal(
    <div className="mj-sheet-overlay">
      <div className="mj-edit-header">
        <button onClick={() => { triggerHaptic(); onCancel(); }}
          style={{ background:"none", border:"none", color:"var(--mj-muted)", fontWeight:700, fontSize:"1rem", padding:"8px", cursor:"pointer" }}>
          Cancel
        </button>
        <div style={{ fontWeight:800, fontSize:"0.92rem", color:"var(--mj-ink)" }}>
          {wordCount}/1000 words
        </div>
        <button onClick={handleSubmit} disabled={submitting}
          style={{
            background:"var(--mj-ink)", color:"var(--mj-card)", border:"none",
            borderRadius:99, padding:"9px 20px", fontWeight:800, fontSize:"0.9rem",
            opacity: submitting ? 0.5 : 1, cursor: submitting ? "not-allowed" : "pointer",
          }}>
          {submitting ? "Publishing…" : "Publish"}
        </button>
      </div>

      <div className="mj-edit-container"
        style={{ background: selectedTheme?.editorBg || "var(--mj-bg)" }}>

        {error && (
          <div style={{ background:"#FEF2F2", color:"#EF4444", padding:"12px 20px", fontWeight:700, fontSize:"0.86rem" }}>
            {error}
          </div>
        )}

        <input className="mj-edit-title-input"
          placeholder="Story Title…"
          value={title} onChange={e => setTitle(e.target.value)}/>

        <div style={{ padding:"0 22px 14px" }}>
          <input
            placeholder="Your pen name"
            value={authorName} onChange={e => setAuthorName(e.target.value)}
            style={{
              background:"transparent", border:"none",
              borderBottom:"2px solid var(--mj-sand)",
              width:"100%", padding:"10px 0", fontSize:"1rem", fontWeight:700,
              color:"var(--mj-ink)", outline:"none", boxSizing:"border-box",
            }}/>
        </div>

        <div className="mj-chip-row" style={{ padding:"0 22px 14px" }}>
          {STORY_THEMES.map(t => (
            <button key={t.id}
              className={`mj-chip ${theme === t.id ? "active" : ""}`}
              style={theme === t.id ? { "--chip-bg": t.color } : {}}
              onClick={() => { triggerHaptic(); setTheme(t.id); }}>
              {t.emoji} {t.label}
            </button>
          ))}
        </div>

        <div className="mj-editor-toolbar">
          {[
            ["bold",      "B", <b style={{ fontSize:"0.88rem" }}>B</b>],
            ["italic",    "I", <i style={{ fontSize:"0.88rem" }}>I</i>],
            ["underline", "U", <u style={{ fontSize:"0.88rem" }}>U</u>],
          ].map(([cmd, key, label]) => (
            <button key={key}
              className={`mj-tool-btn ${fmt[cmd] ? "active" : ""}`}
              onMouseDown={e => { e.preventDefault(); execFormat(cmd); }}>
              {label}
            </button>
          ))}
          <div style={{ width:1, background:"var(--mj-sand)", margin:"0 4px" }}/>
          <button className="mj-tool-btn"
            onMouseDown={e => { e.preventDefault(); execFormat("insertUnorderedList"); }}>•</button>
          <button className="mj-tool-btn"
            onMouseDown={e => { e.preventDefault(); execFormat("insertOrderedList"); }}
            style={{ fontSize:"0.78rem", fontWeight:700 }}>1.</button>
          <button className="mj-tool-btn"
            onMouseDown={e => { e.preventDefault(); execFormat("formatBlock", "blockquote"); }}>"</button>
        </div>

        <div ref={edRef} contentEditable suppressContentEditableWarning
          onInput={handleInput} onKeyUp={syncFmt} onMouseUp={syncFmt}
          style={{
            minHeight:"50vh", padding:"22px", outline:"none",
            fontFamily:"var(--mj-serif)", fontSize:"1.25rem", lineHeight:1.85,
            color:"var(--mj-ink)", wordBreak:"break-word", paddingBottom:160,
          }}
        />
      </div>
    </div>
  , document.body);
}

/* ══════════════════════════════════════════════
   useMyVotes — cached vote loader (C2 fix)
   
   First visit: N parallel getDoc (unavoidable), cache to localStorage.
   Return visit: instant from cache, zero Firestore reads.
   Vote action: updates cache immediately.
══════════════════════════════════════════════ */
const VOTES_CACHE_KEY = "hh_my_votes";

function loadVotesCache() {
  try {
    const raw = localStorage.getItem(VOTES_CACHE_KEY);
    if (!raw) return new Map();
    const obj = JSON.parse(raw);
    return new Map(Object.entries(obj));
  } catch { return new Map(); }
}

function saveVotesCache(map) {
  try {
    const obj = {};
    map.forEach((v, k) => { obj[k] = v; });
    localStorage.setItem(VOTES_CACHE_KEY, JSON.stringify(obj));
  } catch {}
}

function useMyVotes(storyIds) {
  const [votes, setVotes] = useState(() => loadVotesCache());
  const uid = auth.currentUser?.uid;
  const idsKey = storyIds.join(",");

  useEffect(() => {
    if (!uid || storyIds.length === 0) return;

    // Check which IDs are NOT in cache yet
    const cached = loadVotesCache();
    const uncached = storyIds.filter(id => !cached.has(id));

    // If all cached, use cache directly — zero reads
    if (uncached.length === 0) {
      setVotes(cached);
      return;
    }

    let cancelled = false;

    // Only fetch uncached IDs — parallel getDoc
    Promise.all(
      uncached.map(sid =>
        getDoc(doc(db, "storyReactions", sid, "votes", uid))
          .then(snap => snap.exists() ? [sid, snap.data().vote] : [sid, null])
          .catch(() => [sid, null])
      )
    ).then(results => {
      if (cancelled) return;
      const merged = new Map(cached);
      results.forEach(([sid, vote]) => {
        if (vote) merged.set(sid, vote);
      });
      setVotes(merged);
      saveVotesCache(merged);
    });

    return () => { cancelled = true; };
  }, [uid, idsKey]);

  // Expose update function for optimistic cache writes
  const updateVote = useCallback((storyId, vote) => {
    setVotes(prev => {
      const next = new Map(prev);
      if (vote) next.set(storyId, vote);
      else next.delete(storyId);
      saveVotesCache(next);
      return next;
    });
  }, []);

  return { votes, updateVote };
}

/* ══════════════════════════════════════════════
   SHARE SHEET
══════════════════════════════════════════════ */
function ShareSheet({ story, onClose }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = `https://healhub.space`;
  const shareText = `"${story.title}" — a story on HealHub\n${shareUrl}`;

  return createPortal(
    <div className="mj-share-overlay" onClick={onClose}>
      <div className="mj-share-sheet" onClick={e => e.stopPropagation()}>
        <div className="mj-share-handle" />
        <div style={{ fontSize:"0.95rem", fontWeight:800, color:"var(--mj-ink)", marginBottom:4 }}>
          Share this story
        </div>
        <div style={{ fontSize:"0.78rem", color:"var(--mj-muted)", marginBottom:16, lineHeight:1.4 }}>
          "{story.title}"
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:8, width:"100%" }}>
          {typeof navigator !== "undefined" && navigator.share && (
            <button className="mj-share-option" onClick={() => {
              triggerHaptic(); navigator.share({ title: story.title, text: shareText, url: shareUrl }).then(onClose).catch(() => {});
            }}>
              <span style={{ fontSize:"1.1rem" }}>📤</span><span>Share via...</span>
            </button>
          )}
          <button className="mj-share-option" onClick={() => {
            triggerHaptic(); window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank"); onClose();
          }}>
            <span style={{ fontSize:"1.1rem" }}>💬</span><span>WhatsApp</span>
          </button>
          <button className="mj-share-option" onClick={() => {
            triggerHaptic(); navigator.clipboard?.writeText(shareUrl).then(() => { setCopied(true); setTimeout(() => { setCopied(false); onClose(); }, 1200); });
          }}>
            <span style={{ fontSize:"1.1rem" }}>{copied ? "✅" : "🔗"}</span>
            <span>{copied ? "Copied!" : "Copy link"}</span>
          </button>
        </div>
      </div>
    </div>
  , document.body);
}

/* ══════════════════════════════════════════════
   SEARCH OVERLAY — position:fixed (C4 fix)
   Fixed position escapes all scroll containers.
   Inherits CSS vars via var() fallbacks baked into styles.
══════════════════════════════════════════════ */
function SearchOverlay({ value, onChange, onClose }) {
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  return (
    <div className="mj-search-overlay">
      <div className="mj-search-bar">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--mj-muted, #6B7280)" strokeWidth="2.5" style={{ flexShrink:0 }}>
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input ref={inputRef} className="mj-search-overlay-input"
          placeholder="Search stories or authors..."
          value={value} onChange={e => onChange(e.target.value)}
          onKeyDown={e => { if (e.key === "Escape") onClose(); }}
        />
        <button className="mj-search-close" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   ENGAGEMENT ROW — stateless display (C3 fix)
   
   Likes and vote state managed by PARENT via props.
   Only loading/popped/showShare are local (transient UI state).
   No useEffect sync = no stale flash.
══════════════════════════════════════════════ */
const EngagementRow = React.memo(function EngagementRow({
  story, myVote, likes, bookmarks, onBookmarkToggle, onOpenModal,
  onDelete, onVote, isAdmin, variant = "default"
}) {
  const [loading, setLoading] = useState(false);
  const [popped, setPopped]   = useState(false);
  const [showShare, setShowShare] = useState(false);
  const uid = auth.currentUser?.uid;
  const isSelf = !!(uid && story.authorId && uid === story.authorId);
  const canDelete = isSelf || isAdmin;
  const isBookmarked = bookmarks?.has(story.id);
  const isLight = variant === "hero";
  const btnClass = `mj-engage-btn ${isLight ? "mj-engage-light" : ""}`;
  const isLiked = myVote === "like";

  const handleLike = useCallback(async (e) => {
    e.stopPropagation();
    if (!uid || loading) return;
    triggerHaptic("medium");
    setLoading(true);
    setPopped(true);
    setTimeout(() => setPopped(false), 400);
    await onVote(story.id, isLiked ? null : "like", myVote);
    setLoading(false);
  }, [uid, loading, story.id, isLiked, myVote, onVote]);

  const handleDelete = useCallback(async (e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this story?")) return;
    triggerHaptic("heavy");
    try {
      await deleteDoc(doc(db, "stories", story.id));
      StoryCache.remove(story.id);
      onDelete?.(story.id);
    } catch (err) { console.error(err); }
  }, [story.id, onDelete]);

  return (
    <>
      <div className={variant === "hero" ? "mj-hero-engage" : "mj-card-engage"} onClick={e => e.stopPropagation()}>
        <button className={`${btnClass} ${isLiked ? "mj-engage-liked" : ""} ${popped ? "mj-engage-pop" : ""}`}
          onClick={handleLike} style={isLiked ? { padding:"6px 12px" } : {}}>
          <svg width={isLiked ? "16" : "15"} height={isLiked ? "16" : "15"} viewBox="0 0 24 24"
            fill={isLiked ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2.2">
            <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
          </svg>
          <span>{likes}</span>
        </button>

        <button className={btnClass} onClick={(e) => { e.stopPropagation(); triggerHaptic(); onOpenModal(story); }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
          </svg>
          <span>{story.commentCount || 0}</span>
        </button>

        <button className={`${btnClass} mj-engage-secondary`} onClick={(e) => { e.stopPropagation(); triggerHaptic(); setShowShare(true); }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/>
          </svg>
        </button>

        <div style={{ marginLeft:"auto", display:"flex", gap:5, alignItems:"center" }}>
          <button className={`${btnClass} mj-engage-secondary ${isBookmarked ? "mj-engage-saved" : ""}`}
            onClick={(e) => { e.stopPropagation(); onBookmarkToggle?.(story.id); triggerHaptic(); }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill={isBookmarked ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2.2">
              <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/>
            </svg>
          </button>
          {canDelete && (
            <button className={`${btnClass} mj-engage-danger`} onClick={handleDelete}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
              </svg>
            </button>
          )}
        </div>
      </div>
      {showShare && <ShareSheet story={story} onClose={() => setShowShare(false)} />}
    </>
  );
});

/* ══════════════════════════════════════════════
   STORY CARD — React.memo'd (P1 fix)
══════════════════════════════════════════════ */
const StoryCard = React.memo(function StoryCard({ story, isAdmin, bookmarks, onBookmarkToggle, onOpenModal, onDelete, onVote, myVote, likes, index = 0 }) {
  const theme = STORY_THEMES.find(t => t.id === story.theme) || STORY_THEMES[0];
  const readingTime = Math.max(1, Math.ceil((story.wordCount || countWords(story.content || "")) / 200));

  const previewText = useMemo(() =>
    (story.content || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().split(" ").slice(0, 22).join(" "),
  [story.content]);
  const lastLine = useMemo(() => getLastLine(story.content || ""), [story.content]);
  const pubDate = useMemo(() =>
    story.createdAt?.toDate ? story.createdAt.toDate().toLocaleDateString("en-IN", { day:"numeric", month:"short" }) : "",
  [story.createdAt]);

  return (
    <div className="mj-card mj-card-enter" style={{ "--idx": Math.min(index, 7) }}
      onClick={() => { triggerHaptic(); onOpenModal(story); }}>
      <div className="mj-card-cover">
        <ThemeArt theme={theme} height={140}/>
        <span className="mj-card-badge" style={{ color: theme.color }}>{theme.emoji} {theme.label}</span>
      </div>
      <div className="mj-card-body">
        <div className="mj-card-author-row">
          <div className="mj-card-av" style={{ background: theme.light, color: theme.color }}>
            {(story.authorName || "A")[0].toUpperCase()}
          </div>
          <div className="mj-card-author-info">
            <div className="mj-card-author-name">{story.authorName || "Anonymous"}</div>
            {pubDate && <div className="mj-card-author-date">{pubDate} · {readingTime} min read</div>}
          </div>
        </div>
        <h3 className="mj-card-title">{story.title}</h3>
        {previewText && <p className="mj-card-preview">{previewText}…</p>}
        {lastLine && <p className="mj-card-lastline">"{lastLine}"</p>}
        <EngagementRow story={story} myVote={myVote} likes={likes} bookmarks={bookmarks}
          onBookmarkToggle={onBookmarkToggle} onOpenModal={onOpenModal}
          onDelete={onDelete} onVote={onVote} isAdmin={isAdmin} />
      </div>
    </div>
  );
});

/* ══════════════════════════════════════════════
   COMPACT CARD — React.memo'd
══════════════════════════════════════════════ */
const CompactCard = React.memo(function CompactCard({ story, isAdmin, bookmarks, onBookmarkToggle, onOpenModal, onDelete, onVote, myVote, likes, index = 0 }) {
  const theme = STORY_THEMES.find(t => t.id === story.theme) || STORY_THEMES[0];
  const readingTime = Math.max(1, Math.ceil((story.wordCount || countWords(story.content || "")) / 200));

  return (
    <div className="mj-compact mj-card-enter" style={{ "--idx": Math.min(index, 7) }}
      onClick={() => { triggerHaptic(); onOpenModal(story); }}>
      <div className="mj-compact-art"><ThemeArt theme={theme} height="100%"/></div>
      <div className="mj-compact-body">
        <div style={{ fontSize:"0.68rem", color:theme.color, fontWeight:700, marginBottom:3, display:"flex", alignItems:"center", gap:4 }}>
          <span>{theme.emoji}</span><span>{theme.label}</span>
          <span style={{ color:"var(--mj-muted)", fontWeight:500 }}>· {readingTime} min</span>
        </div>
        <h3 className="mj-compact-title">{story.title}</h3>
        <div className="mj-compact-author">{story.authorName || "Anonymous"}</div>
        <EngagementRow story={story} myVote={myVote} likes={likes} bookmarks={bookmarks}
          onBookmarkToggle={onBookmarkToggle} onOpenModal={onOpenModal}
          onDelete={onDelete} onVote={onVote} isAdmin={isAdmin} />
      </div>
    </div>
  );
});

/* ══════════════════════════════════════════════
   TEXT CARD — React.memo'd
══════════════════════════════════════════════ */
const TextCard = React.memo(function TextCard({ story, isAdmin, bookmarks, onBookmarkToggle, onOpenModal, onDelete, onVote, myVote, likes, index = 0 }) {
  const theme = STORY_THEMES.find(t => t.id === story.theme) || STORY_THEMES[0];
  const readingTime = Math.max(1, Math.ceil((story.wordCount || countWords(story.content || "")) / 200));
  const pullQuote = useMemo(() => getLastLine(story.content || ""), [story.content]);
  const pubDate = useMemo(() =>
    story.createdAt?.toDate ? story.createdAt.toDate().toLocaleDateString("en-IN", { day:"numeric", month:"short" }) : "",
  [story.createdAt]);

  return (
    <div className="mj-text-card mj-card-enter" style={{ "--idx": Math.min(index, 7), "--tc": theme.color, "--tl": theme.light }}
      onClick={() => { triggerHaptic(); onOpenModal(story); }}>
      <div className="mj-text-header">
        <div className="mj-text-av" style={{ background: theme.light, color: theme.color }}>
          {(story.authorName || "A")[0].toUpperCase()}
        </div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:"0.82rem", fontWeight:700, color:"var(--mj-ink)" }}>{story.authorName || "Anonymous"}</div>
          <div style={{ fontSize:"0.68rem", color:"var(--mj-muted)" }}>{pubDate} · {readingTime} min read</div>
        </div>
        <div className="mj-text-badge" style={{ background: theme.light, color: theme.color }}>
          {theme.emoji} {theme.label}
        </div>
      </div>
      <h3 className="mj-text-title">{story.title}</h3>
      {pullQuote && <div className="mj-text-quote" style={{ borderLeftColor: theme.color }}>"{pullQuote}"</div>}
      <EngagementRow story={story} myVote={myVote} likes={likes} bookmarks={bookmarks}
        onBookmarkToggle={onBookmarkToggle} onOpenModal={onOpenModal}
        onDelete={onDelete} onVote={onVote} isAdmin={isAdmin} />
    </div>
  );
});

/* ══════════════════════════════════════════════
   HERO CARD — React.memo'd
══════════════════════════════════════════════ */
const HeroCard = React.memo(function HeroCard({ story, onOpenModal, bookmarks, onBookmarkToggle, onDelete, onVote, isAdmin, myVote, likes }) {
  const theme = STORY_THEMES.find(t => t.id === story.theme) || STORY_THEMES[0];
  const readingTime = Math.max(1, Math.ceil((story.wordCount || countWords(story.content || "")) / 200));

  return (
    <div className="mj-hero-card mj-card-enter" style={{ "--idx": 0 }}
      onClick={() => { triggerHaptic(); onOpenModal(story); }}>
      <div className="mj-hero-bg"><ThemeArt theme={theme} height="100%"/></div>
      <div className="mj-hero-overlay"/>
      <div className="mj-hero-content">
        <div className="mj-hero-tag" style={{ color: theme.light }}>{theme.emoji} {theme.label}</div>
        <h2 className="mj-hero-title">{story.title}</h2>
        <div className="mj-hero-meta">
          <div className="mj-hero-avatar">{(story.authorName || "A")[0].toUpperCase()}</div>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:800, fontSize:"0.92rem" }}>{story.authorName || "Anonymous"}</div>
            <div className="mj-hero-stats"><span>⏱ {readingTime} min</span></div>
          </div>
        </div>
        <EngagementRow story={story} myVote={myVote} likes={likes} bookmarks={bookmarks}
          onBookmarkToggle={onBookmarkToggle} onOpenModal={onOpenModal}
          onDelete={onDelete} onVote={onVote} isAdmin={isAdmin} variant="hero" />
      </div>
    </div>
  );
});

/* ══════════════════════════════════════════════
   MAIN FEED — v10.2 (production-ready)
   
   C1 FIX: No height:100dvh, no overflow:hidden on .mj-root.
           .mj-root is min-height:100% and flows naturally.
           .mj-feed-scroll has NO overflow — parent .hh-screen scrolls.
           .mj-nav-strip uses position:sticky to stay pinned.
           Result: single scroll container, zero iOS scroll fights.
══════════════════════════════════════════════ */
export default function MobileJournal() {
  // ── CSS injection (C5 fix: fonts loaded separately, no @import in CSS) ──
  useLayoutEffect(() => {
    // Preload fonts separately — avoids render-blocking @import
    if (!document.getElementById("mj-fonts-v10")) {
      const link = document.createElement("link");
      link.id = "mj-fonts-v10";
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap";
      document.head.appendChild(link);
    }
    if (!document.getElementById("mj-css-v10")) {
      const el = document.createElement("style");
      el.id = "mj-css-v10";
      el.textContent = ELITE_JOURNAL_CSS;
      document.head.appendChild(el);
    }
    const old9 = document.getElementById("mj-css-v9");
    if (old9) old9.remove();
    const old = document.getElementById("mj-css");
    if (old) old.remove();
  }, []);

  const [showWriteModal,      setShowWriteModal]      = useState(false);
  const [showWellnessReport,  setShowWellnessReport]  = useState(false);
  const [showSearch,          setShowSearch]           = useState(false);
  const [feedTab, setFeedTab]         = useState("foryou");
  const [stories, setStories]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeTheme, setActiveTheme] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [hasMore, setHasMore]         = useState(true);
  const [isAdmin, setIsAdmin]         = useState(false);
  const [modalStory, setModalStory]   = useState(null);

  const { bookmarks, toggle: toggleBookmark } = useBookmarks();
  const uid = auth.currentUser?.uid;

  const sentinelRef   = useRef(null);
  const fetchingRef   = useRef(false);
  const lastDocRef    = useRef(null);
  const hasMoreRef    = useRef(true);

  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);
  useEffect(() => {
    if (!uid) return;
    getDoc(doc(db, "users", uid))
      .then(s => { if (s.exists() && s.data().role === "admin") setIsAdmin(true); })
      .catch(() => {});
  }, [uid]);

  // ── Batch vote loader with localStorage cache ──
  const storyIds = useMemo(() => stories.map(s => s.id), [stories]);
  const { votes: myVotes, updateVote } = useMyVotes(storyIds);

  // ── Centralized vote handler — manages Firestore + optimistic cache ──
  const handleVote = useCallback(async (storyId, newVote, oldVote) => {
    if (!uid) return;
    const vRef = doc(db, "storyReactions", storyId, "votes", uid);
    const sRef = doc(db, "stories", storyId);

    // Optimistic update
    updateVote(storyId, newVote);
    if (newVote === null && oldVote === "like") {
      setStories(prev => prev.map(s => s.id === storyId ? { ...s, likes: Math.max(0, (s.likes||0) - 1) } : s));
    } else if (newVote === "like" && oldVote !== "like") {
      setStories(prev => prev.map(s => s.id === storyId ? { ...s, likes: (s.likes||0) + 1 } : s));
    }

    try {
      if (newVote === null) {
        // Removing vote
        await deleteDoc(vRef);
        await updateDoc(sRef, { [oldVote === "like" ? "likes" : "dislikes"]: increment(-1) });
      } else {
        // Adding/changing vote
        if (oldVote && oldVote !== newVote) {
          await updateDoc(sRef, { [oldVote === "like" ? "likes" : "dislikes"]: increment(-1) });
        }
        await setDoc(vRef, { vote: newVote });
        await updateDoc(sRef, { [newVote === "like" ? "likes" : "dislikes"]: increment(1) });
      }
    } catch {
      // Rollback on failure
      updateVote(storyId, oldVote);
      // Refetch to get accurate count
      const snap = await getDoc(sRef);
      if (snap.exists()) {
        setStories(prev => prev.map(s => s.id === storyId ? { ...s, likes: snap.data().likes || 0 } : s));
      }
    }
  }, [uid, updateVote]);

  // ── Fetch logic (unchanged) ──
  const fetchStories = useCallback(async (append = false) => {
    if (fetchingRef.current) return;
    if (append && !hasMoreRef.current) return;

    if (!append) {
      const cached = StoryCache.get(activeTheme);
      if (cached) {
        setStories(cached.stories);
        setHasMore(cached.hasMore);
        lastDocRef.current = cached.lastDoc ?? null;
        setLoading(false);
        return;
      }
    }

    fetchingRef.current = true;
    if (!append) setLoading(true); else setLoadingMore(true);

    try {
      const cursor = append ? lastDocRef.current : null;
      const constraints = [
        ...(activeTheme !== "all" ? [where("theme", "==", activeTheme)] : []),
        orderBy("createdAt", "desc"),
        ...(cursor ? [startAfter(cursor)] : []),
        limit(PAGE_SIZE),
      ];
      const q    = query(collection(db, "stories"), ...constraints);
      const snap = await getDocs(q);
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const newLastDoc = snap.docs[snap.docs.length - 1] ?? null;
      const more = snap.docs.length === PAGE_SIZE;
      lastDocRef.current = newLastDoc;

      if (append) {
        setStories(prev => mergeUniqueStories(prev, docs));
        StoryCache.appendPage(activeTheme, { newDocs: docs, newLastDoc, hasMore: more });
      } else {
        setStories(docs);
        StoryCache.set(activeTheme, { stories: docs, lastDoc: newLastDoc, hasMore: more });
      }
      setHasMore(more);
    } catch (e) { console.error("fetchStories error:", e); }
    finally { fetchingRef.current = false; setLoading(false); setLoadingMore(false); }
  }, [activeTheme]);

  useEffect(() => {
    lastDocRef.current = null; hasMoreRef.current = true;
    setHasMore(true); setStories([]); setLoading(true);
    fetchStories(false);
  }, [activeTheme, fetchStories]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      entries => { if (entries[0].isIntersecting && hasMoreRef.current && !fetchingRef.current) fetchStories(true); },
      { rootMargin: "240px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [fetchStories]);

  // ── Display logic (P3 fix: spread before sort for trending) ──
  const displayedStories = useMemo(() => {
    let list = [...stories];
    if (feedTab === "bookmarks") {
      list = list.filter(s => bookmarks.has(s.id));
    } else if (feedTab === "following") {
      try {
        const f = new Set(JSON.parse(localStorage.getItem("hh_following") || "[]"));
        list = list.filter(s => f.has(s.authorId));
      } catch { list = []; }
    } else if (feedTab === "trending") {
      list = [...list].sort((a, b) => ((b.likes||0)*3 + (b.commentCount||0)) - ((a.likes||0)*3 + (a.commentCount||0)));
    }
    if (debouncedSearch.trim()) {
      const q = debouncedSearch.toLowerCase().trim();
      list = list.filter(s => s.title?.toLowerCase().includes(q) || s.authorName?.toLowerCase().includes(q));
    }
    return list;
  }, [stories, feedTab, bookmarks, debouncedSearch]);

  const heroStory = useMemo(() => {
    if (!displayedStories.length || debouncedSearch) return null;
    if (feedTab !== "foryou" && feedTab !== "trending") return null;
    return [...displayedStories].sort((a, b) => (b.likes || 0) - (a.likes || 0))[0];
  }, [displayedStories, debouncedSearch, feedTab]);

  const gridStories = useMemo(() =>
    heroStory ? displayedStories.filter(s => s.id !== heroStory.id) : displayedStories,
  [displayedStories, heroStory]);

  const handleDeleteLocally = useCallback(id => {
    StoryCache.remove(id);
    setStories(prev => prev.filter(s => s.id !== id));
    if (modalStory?.id === id) setModalStory(null);
  }, [modalStory]);

  const handleWriteSuccess = useCallback((newStory) => {
    triggerHaptic("heavy");
    setShowWriteModal(false);
    setStories(prev => mergeUniqueStories([newStory], prev));
  }, []);

  // ── Shared props factory for card rendering ──
  const cardProps = useCallback((story, index) => ({
    story, index, isAdmin, bookmarks,
    onBookmarkToggle: toggleBookmark,
    onOpenModal: setModalStory,
    onDelete: handleDeleteLocally,
    onVote: handleVote,
    myVote: myVotes.get(story.id) || null,
    likes: story.likes || 0,
  }), [isAdmin, bookmarks, toggleBookmark, handleDeleteLocally, handleVote, myVotes]);

  function renderCard(story, index) {
    const props = cardProps(story, index);
    // #42 fix: use story.id hash for layout type, not index.
    // This ensures the same story always gets the same card type
    // even when the list shifts (new story prepended).
    const hash = story.id.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
    if (hash % 7 === 0) return <TextCard key={story.id} {...props} />;
    if (hash % 5 === 0) return <CompactCard key={story.id} {...props} />;
    return <StoryCard key={story.id} {...props} />;
  }

  return (
    <div className="mj-root">

      {/* ── STICKY NAV (sticks inside parent scroll container) ── */}
      <div className="mj-nav-strip">
        <div className="mj-nav-row">
          <div className="mj-nav-tabs">
            {[
              { key:"foryou",    label:"For you" },
              { key:"trending",  label:"Trending" },
              { key:"following", label:"Following" },
              { key:"bookmarks", label:`Saved${bookmarks.size > 0 ? ` (${bookmarks.size})` : ""}` },
            ].map(tab => (
              <button key={tab.key}
                className={`mj-nav-tab ${feedTab === tab.key ? "active" : ""}`}
                onClick={() => { triggerHaptic(); setFeedTab(tab.key); }}>
                {tab.label}
              </button>
            ))}
          </div>
          <div className="mj-nav-actions">
            <button className="mj-nav-icon-btn" onClick={() => { triggerHaptic(); setShowSearch(true); }} aria-label="Search">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </button>
            <button className="mj-nav-icon-btn mj-nav-write" onClick={() => { triggerHaptic("heavy"); setShowWriteModal(true); }} aria-label="Write">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
            </button>
            <button className="mj-nav-icon-btn" onClick={() => { triggerHaptic(); setShowWellnessReport(true); }} aria-label="Wellness">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 20V10"/><path d="M12 20V4"/><path d="M6 20v-6"/>
              </svg>
            </button>
          </div>
        </div>
        <div className="mj-theme-strip">
          <button className={`mj-theme-pill ${activeTheme === "all" ? "active" : ""}`}
            onClick={() => { triggerHaptic(); setActiveTheme("all"); }}>All</button>
          {STORY_THEMES.map(t => (
            <button key={t.id}
              className={`mj-theme-pill ${activeTheme === t.id ? "active" : ""}`}
              style={activeTheme === t.id ? { "--tp-color": t.color, "--tp-light": t.light } : {}}
              onClick={() => { triggerHaptic(); setActiveTheme(t.id); }}>{t.emoji}</button>
          ))}
        </div>
      </div>

      {/* ── SEARCH (C4 fix: position:fixed, escapes all containers) ── */}
      {showSearch && <SearchOverlay value={searchQuery} onChange={setSearchQuery} onClose={() => setShowSearch(false)} />}

      {/* ── FEED (C1 fix: NO overflow-y, parent scrolls) ── */}
      <div className="mj-feed-area">

        {loading && stories.length === 0 && (
          <>
            <div className="mj-skel-card" style={{ height:340, borderRadius:24, flexShrink:0 }}>
              <div className="mj-skel-base" style={{ height:"100%", borderRadius:24 }}/>
            </div>
            {[1,2,3].map(i => <SkeletonCard key={i}/>)}
          </>
        )}

        {!loading && displayedStories.length === 0 && (
          <div className="mj-empty">
            <span className="mj-empty-icon">
              {feedTab === "bookmarks" ? "🔖" : feedTab === "following" ? "👥" : "📝"}
            </span>
            <h3 style={{ fontFamily:"var(--mj-serif)", fontSize:"1.8rem", color:"var(--mj-ink)", marginBottom:10, fontWeight:700 }}>
              Nothing here yet
            </h3>
            <p style={{ color:"var(--mj-muted)", fontSize:"0.95rem", fontWeight:500 }}>
              {feedTab === "bookmarks" ? "Save stories to read them later."
                : feedTab === "following" ? "Follow authors to see their stories here."
                : debouncedSearch ? `No stories match "${debouncedSearch}".`
                : "Be the first to share your story."}
            </p>
            {!debouncedSearch && (feedTab === "foryou" || feedTab === "trending") && (
              <button className="mj-empty-write" onClick={() => { triggerHaptic("heavy"); setShowWriteModal(true); }}>
                Start writing ✍️
              </button>
            )}
          </div>
        )}

        {heroStory && <HeroCard {...cardProps(heroStory, 0)} />}

        {!loading && gridStories.length > 0 && (
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            {gridStories.map((story, i) => renderCard(story, i))}
          </div>
        )}

        <div ref={sentinelRef} className="mj-sentinel"/>

        {loadingMore && (
          <div className="mj-load-more"><div className="mj-spinner"/><span>Loading more stories…</span></div>
        )}
        {!hasMore && stories.length > 0 && !debouncedSearch && (
          <div style={{ textAlign:"center", padding:"20px 0 8px", color:"var(--mj-muted)", fontSize:"0.78rem", fontWeight:600 }}>
            ✦ You've reached the end ✦
          </div>
        )}
      </div>

      {/* ── FAB ── */}
      <button className="mj-fab" onClick={() => { triggerHaptic("heavy"); setShowWriteModal(true); }} aria-label="Write">✍️</button>

      {/* ── MODALS ── */}
      {modalStory && <StoryModal story={modalStory} onClose={() => { triggerHaptic(); setModalStory(null); }} isAdmin={isAdmin} onDelete={handleDeleteLocally} />}
      {showWriteModal && <WriteStoryForm onSuccess={handleWriteSuccess} onCancel={() => { triggerHaptic(); setShowWriteModal(false); }} />}
      {showWellnessReport && <WellnessReportSheet onClose={() => setShowWellnessReport(false)} />}
    </div>
  );
}
